import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMessagesByContactIdWithPagination, sendMessage, sendFile, sendAudioMessage, sendSystemMessage, sendGroupMessage, sendGroupImage, sendGroupDocument, sendGroupAudioMessage, fileToBase64, blobToBase64 } from '../services/MessageService';
import { getContacts, getContactsWithFilters, updateContact, createContact, deleteContact, archiveContact, markContactAsViewed, markContactAsUnread, setContactAIActive, notifyContactResponsible } from '../services/ContactService';
import { getGroupsBySector, getGroupMessages, markGroupAsViewed, deleteGroupFromLigchat, type GroupListDto, type GroupMessageDto } from '../services/GroupService';
import { getAllUsers } from '../services/UserService';
import { getTags } from '../services/LabelService';
import { getSector } from '../services/SectorService';
import SessionService from '../services/SessionService';
import { AudioRecorder } from '../components/AudioRecorder';
import { ContactItem } from '../components/ContactItem';
import { AudioMessage } from '../components/AudioMessage';
import { ImageViewer } from '../components/ImageViewer';
import EmojiPicker from '../components/EmojiPicker';
import MessageReactionBar from '../components/MessageReactionBar';
import ContactShareCard from '../components/ContactShareCard';
import InteractiveMessageBubble from '../components/InteractiveMessageBubble';
import ForwardedMessageHeader from '../components/ForwardedMessageHeader';
import ChatWebSocketService from '../services/ChatWebSocketService';
import WebSocketStatus from '../components/WebSocketStatus';
import dayjs, { createAppDate, toAppTimezone, APP_TIMEZONE, normalizeSentAtToLocal, formatMessageTime } from '../utils/dayjsConfig';
import { formatWhatsAppText, emojiMartToUnicode } from '../utils/whatsappFormatting';
import { outgoingTextMatches } from '../utils/outgoingMessageMatch';
import { mergeContactsList } from '../utils/mergeContactsList';
import { sendReaction } from '../services/UnofficialMessageService';
import { processAvatarUrl } from '../utils/avatarUtils';
import { playMessageNotificationSound } from '../utils/playMessageNotificationSound';
import { maybePlayIncomingGroupMessageSound } from '../utils/groupMessageIncomingSound';
import { parseContactShareContent, formatPhoneForCreateContact, contactMatchesPhone, findContactByDigitsOnServer } from '../utils/contactShareMessage';
import { useNotification } from '../contexts/NotificationContext';
import { FiPhone, FiMoreVertical, FiUserPlus, FiTag, FiUsers, FiUser, FiEdit2, FiArrowLeft, FiArchive, FiSmile } from 'react-icons/fi';
import './ChatMobile.css';
import '../styles/components/ContactItem.css';

// Interfaces e tipos (mesmas do ChatWeb)
interface OriginalContactData {
  id: number;
  name: string;
  tagId: number | null;
  number: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  contactStatus: string;
  aiActive: boolean;
  assignedTo: number | null;
  assignedToName?: string | null;
  responsibleName?: string | null;
  createdAt: string;
  profilePicture?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  is_viewed: boolean;
  archived?: boolean;
  order?: number;
  isViewed?: boolean;
}

interface Message {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  url?: string | null;
  attachment?: { url?: string; name?: string; type?: string } | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  status?: 'sending' | 'sent' | 'error';
  messageStatus?: 'sent' | 'delivered' | 'received' | 'read';
  whatsappMessageID?: string;
  replyToMessageID?: string;
  repliedMessage?: Message;
  replyTo?: Message;
  senderName?: string;
  senderUserId?: number;
  isAnonymous?: boolean;
  reactionEmoji?: string | null;
  operatorReactionEmoji?: string | null;
  isForwarded?: boolean;
}

type ContactData = OriginalContactData;

const MESSAGE_LONG_PRESS_MS = 500;

// Codifica caracteres não-ASCII / reservados (colchetes, espaços, acentos) que
// quebram `href`/`src` em alguns navegadores quando o backend devolve a URL
// sem encoding. URLs já codificadas passam intactas.
const toSafeUrl = (raw: string): string => {
  try {
    return new URL(raw).toString();
  } catch {
    try {
      return encodeURI(raw);
    } catch {
      return raw;
    }
  }
};

const getMessageMediaUrl = (m: { mediaUrl?: string | null; url?: string | null; attachment?: { url?: string } | null }) => {
  const raw = m.mediaUrl || (m as any).url || m.attachment?.url || null;
  return raw ? toSafeUrl(raw) : null;
};

const getMessagePreviewText = (msg: { content?: string | null; mediaType?: string; fileName?: string | null }): string => {
  if (!msg) return '';
  if (msg.mediaType === 'image') return 'Imagem';
  if (msg.mediaType === 'sticker') return 'Figurinha';
  if (msg.mediaType === 'gif') return 'GIF';
  if (msg.mediaType === 'audio' || msg.mediaType === 'voice') return 'Áudio';
  if (msg.mediaType === 'video') return 'Vídeo';
  if (msg.mediaType === 'document') return msg.fileName ? `📎 ${msg.fileName}` : '📎 Documento';
  if (msg.mediaType === 'interactive') {
    try {
      const j = JSON.parse((msg.content || '').trim()) as { previewText?: string };
      const pt = (j?.previewText || '').trim();
      if (pt) return pt.split('\n')[0]?.slice(0, 120) || 'Mensagem interativa';
    } catch {
      /* ignore */
    }
    return 'Mensagem interativa';
  }
  if (msg.mediaType === 'system') return '';
  return (msg.content || '').trim();
};

const normalizeContactLastMessage = (c: any) => ({
  ...c,
  lastMessage: c.lastMessage ?? c.last_message ?? c.lastMessageContent ?? '',
  lastMessageTime: c.lastMessageTime ?? c.last_message_time ?? c.lastMessageSentAt ?? c.updatedAt ?? c.createdAt
});

type ChatMobileProps = {
  hasNewMessages?: { [contactId: number]: boolean };
  setHasNewMessages?: (newState: { [contactId: number]: boolean } | ((prev: { [contactId: number]: boolean }) => { [contactId: number]: boolean })) => void;
  contacts?: ContactData[];
  setContacts?: React.Dispatch<React.SetStateAction<ContactData[]>>;
};

// Componentes de ícones
const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const ChatMobile: React.FC<ChatMobileProps> = ({
  hasNewMessages: externalHasNewMessages,
  setHasNewMessages,
  contacts: externalContacts,
  setContacts: externalSetContacts
}) => {
  // Estados principais
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupListDto | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [groupMessages, setGroupMessages] = useState<GroupMessageDto[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [contacts, setContacts] = useState<ContactData[]>(externalContacts || []);
  const [groups, setGroups] = useState<GroupListDto[]>([]);
  const [groupPhotoRefreshAt, setGroupPhotoRefreshAt] = useState(() => Math.floor(Date.now() / 120000));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const [showGroupsView, setShowGroupsView] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const showScrollToBottomRef = useRef(false);
  const showScrollToBottomGroupRef = useRef(false);
  const [hasNewMessages, setLocalHasNewMessages] = useState<{ [contactId: number]: boolean }>(externalHasNewMessages || {});

  // Estados específicos do mobile
  const [showContactsList, setShowContactsList] = useState(true);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [showNameEditModal, setShowNameEditModal] = useState(false);

  // Estados para funcionalidades
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [isTagLoading, setIsTagLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(SessionService.getCurrentUserId());
  const [currentUserName, setCurrentUserName] = useState<string | null>(SessionService.getCurrentUserName());
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(SessionService.isCurrentUserAdmin());
  const [isAnonymous, setIsAnonymous] = useState(() => SessionService.getIsAnonymous());

  // Estados para filtros e modais
  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [archivedContactsCount, setArchivedContactsCount] = useState(0);
  const [showRecentContacts, setShowRecentContacts] = useState(false);
  const [recentContactsCount, setRecentContactsCount] = useState(0);
  const [showPendingContacts, setShowPendingContacts] = useState(false);
  const [pendingContactsCount, setPendingContactsCount] = useState(0);
  const { addNotification, removeByContactId } = useNotification();
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [newContactError, setNewContactError] = useState('');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedFilterUser, setSelectedFilterUser] = useState<number | null>(null);
  const [selectedFilterTag, setSelectedFilterTag] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchivingContact, setIsArchivingContact] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  // Estados para visualizador de imagem
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  // Estado para expandir foto do contato (lista e header)
  const [showAvatarViewer, setShowAvatarViewer] = useState(false);
  const [avatarViewerUrl, setAvatarViewerUrl] = useState('');
  const [avatarViewerAlt, setAvatarViewerAlt] = useState('');

  const [showForwardContactModal, setShowForwardContactModal] = useState(false);
  const [selectedContactToForward, setSelectedContactToForward] = useState<ContactData | null>(null);
  const [isForwardingContact, setIsForwardingContact] = useState(false);
  const [forwardContactSearch, setForwardContactSearch] = useState('');
  const [forwardListOpen, setForwardListOpen] = useState(false);
  const [contactShareBusyMessageId, setContactShareBusyMessageId] = useState<number | null>(null);

  // Estados para responder mensagens específicas
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [replyPreview, setReplyPreview] = useState('');
  const [replyingToGroupMessage, setReplyingToGroupMessage] = useState<GroupMessageDto | null>(null);

  // Modal de anexo (imagem/documento): Baixar ou Responder
  const [attachmentModal, setAttachmentModal] = useState<{
    url: string;
    fileName: string;
    type: 'image' | 'document';
    message?: Message;
    groupMessage?: GroupMessageDto;
  } | null>(null);

  /** Long-press na mensagem 1:1: responder ou reagir */
  const [messageActionSheet, setMessageActionSheet] = useState<Message | null>(null);

  // Estados para microfone
  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState(false);
  const [isCheckingMicrophone, setIsCheckingMicrophone] = useState(true);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const groupMessagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const emojiAnchorRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const groupImageInputRef = useRef<HTMLInputElement>(null);
  const groupDocumentInputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isUserScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const isContactsRefreshingRef = useRef(false);
  const contactsRef = useRef<ContactData[]>([]);
  contactsRef.current = contacts;
  const selectedContactRef = useRef<ContactData | null>(null);
  selectedContactRef.current = selectedContact;
  const selectedGroupRef = useRef<GroupListDto | null>(null);
  selectedGroupRef.current = selectedGroup;
  const previousWaitingIdsRef = useRef<Set<number>>(new Set());
  const prevMessagesLenRef = useRef(0);
  const longPressTimerRef = useRef<number | null>(null);
  const suppressReplyClickRef = useRef(false);
  const prevLastMessageIdRef = useRef<number | null>(null);
  const isLoadingMoreRef = useRef(false);
  const scrollRestoreRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);
  const groupIncomingSoundSkipRef = useRef(true);

  // Para debug: no console do navegador, execute: window.__DEBUG_CHAT_SCROLL__ = true
  const DEBUG_SCROLL = typeof window !== 'undefined' && !!(window as any).__DEBUG_CHAT_SCROLL__;

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const handleContactSelectRef = useRef<(contact: ContactData) => Promise<void>>(async () => {});

  // Constantes
  const MESSAGES_PAGE_SIZE = 10;
  const formatCount = (n: number) => n >= 1000 ? `${Math.floor(n / 1000)}k+` : String(n);

  // Sincronizar hasNewMessages externo com local
  useEffect(() => {
    if (externalHasNewMessages) {
      setLocalHasNewMessages(externalHasNewMessages);
    }
  }, [externalHasNewMessages]);

  // Função para atualizar hasNewMessages
  const updateHasNewMessages = useCallback((newState: { [contactId: number]: boolean } | ((prev: { [contactId: number]: boolean }) => { [contactId: number]: boolean })) => {
    if (typeof newState === 'function') {
      setLocalHasNewMessages(prev => {
        const updated = newState(prev);
        if (setHasNewMessages) {
          setHasNewMessages(updated);
        }
        return updated;
      });
    } else {
      setLocalHasNewMessages(newState);
      if (setHasNewMessages) {
        setHasNewMessages(newState);
      }
    }
  }, [setHasNewMessages]);

  // Função para formatar número de telefone
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';

    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    return phone;
  };

  // Função para extrair iniciais do nome
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '??';
    return name
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Formatar cabeçalho de data (Hoje, Ontem ou data)
  const formatDateHeader = (dateIso: string): string => {
    const d = toAppTimezone(dateIso);
    if (!d.isValid()) return '';
    const now = createAppDate();
    const diffDays = d.startOf('day').diff(now.startOf('day'), 'day');
    if (diffDays === 0) return 'Hoje';
    if (diffDays === -1) return 'Ontem';
    return d.format('DD/MM/YYYY');
  };

  const scrollToBottom = useCallback(() => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      });
    }
  }, []);

  // Scroll para baixo no chat do grupo (mesmo modelo do chat de contato)
  const scrollToBottomGroup = useCallback(() => {
    try {
      const container = groupMessagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    } catch {
      // Silenciar erros
    }
  }, []);

  // Só faz scroll quando mídia carrega se o usuário estiver perto do final (evita scroll ao carregar imagens de mensagens antigas)
  const scrollToBottomIfNearBottom = useCallback(() => {
    if (!showScrollToBottomRef.current) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  // Só faz scroll no grupo quando mídia carrega se o usuário estiver perto do final
  const scrollToBottomGroupIfNearBottom = useCallback(() => {
    if (!showScrollToBottomGroupRef.current) {
      scrollToBottomGroup();
    }
  }, [scrollToBottomGroup]);

  // Restaurar scroll após "Ver mais" - useLayoutEffect roda ANTES da pintura (técnica Kirby)
  useLayoutEffect(() => {
    const data = scrollRestoreRef.current;
    if (!data) return;

    const c = messagesContainerRef.current;
    if (!c) {
      scrollRestoreRef.current = null;
      isLoadingMoreRef.current = false;
      return;
    }

    const { prevScrollHeight, prevScrollTop } = data;
    scrollRestoreRef.current = null;

    const newScrollTop = prevScrollTop + (c.scrollHeight - prevScrollHeight);
    c.scrollTop = newScrollTop;

    if (DEBUG_SCROLL) console.log('[ChatMobile] useLayoutEffect RESTORE:', { prevScrollHeight, prevScrollTop, newScrollHeight: c.scrollHeight, newScrollTop });

    requestAnimationFrame(() => {
      if (c && scrollRestoreRef.current === null) {
        c.scrollTop = prevScrollTop + (c.scrollHeight - prevScrollHeight);
        if (DEBUG_SCROLL) console.log('[ChatMobile] useLayoutEffect RESTORE (rAF)');
      }
      isLoadingMoreRef.current = false;
    });
  }, [messages]);

  // Scroll automático ao receber nova mensagem (não ao carregar mais antigas)
  useEffect(() => {
    const len = messages.length;
    const prevLen = prevMessagesLenRef.current;
    const lastMsg = messages[len - 1];
    const prevLastId = prevLastMessageIdRef.current;

    prevMessagesLenRef.current = len;
    prevLastMessageIdRef.current = lastMsg?.id ?? null;

    if (len <= prevLen || prevLen === 0) return;
    if (isLoadingMoreRef.current) return;
    if (prevLen > 0 && lastMsg?.id === prevLastId) {
      if (DEBUG_SCROLL) console.log('[ChatMobile] useEffect SKIP: append (lastMsgId igual)', lastMsg?.id);
      return;
    }

    if (!showScrollToBottomRef.current || lastMsg?.isSent) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Scroll no grupo: apenas quando um grupo é selecionado pela primeira vez
  const prevGroupRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedGroup && selectedGroup.id !== prevGroupRef.current) {
      prevGroupRef.current = selectedGroup.id;
      setTimeout(() => scrollToBottomGroup(), 100);
    }
  }, [selectedGroup, scrollToBottomGroup]);

  // Função para navegar para a mensagem original de uma reply
  const scrollToRepliedMessage = useCallback((messageId: number) => {
    // Encontrar o elemento da mensagem
    const messageElement = document.getElementById(`message-${messageId}`);

    if (messageElement) {
      // Scroll suave para a mensagem
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Destacar a mensagem
      messageElement.classList.add('cmob-message-highlight');
      setTimeout(() => {
        messageElement.classList.remove('cmob-message-highlight');
      }, 2000);
    }
  }, []);

  // Função para responder a uma mensagem específica
  const handleReplyToMessage = useCallback((message: Message) => {
    console.log('Mensagem selecionada para resposta (Mobile):', {
      message,
      whatsappMessageID: message.whatsappMessageID,
      hasWhatsappMessageID: message.whatsappMessageID ? 'SIM' : 'NÃO'
    });

    setReplyingTo(message);
    setReplyPreview(message.content || 'Arquivo/Áudio');
    // Focar no input
    setTimeout(() => {
      const textarea = document.querySelector('.cmob-input') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  }, []);

  // Função para cancelar resposta
  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyPreview('');
  }, []);

  const handleReplyToGroupMessage = useCallback((gm: GroupMessageDto) => {
    setReplyingToGroupMessage(gm);
  }, []);

  const handleCancelReplyGroup = useCallback(() => {
    setReplyingToGroupMessage(null);
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessagePointerDown = useCallback((message: Message) => {
    if (message.mediaType === 'system') return;
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null;
      suppressReplyClickRef.current = true;
      setMessageActionSheet(message);
      try {
        navigator.vibrate?.(20);
      } catch {
        /* ignore */
      }
    }, MESSAGE_LONG_PRESS_MS);
  }, [clearLongPressTimer]);

  const handleMessagePointerEnd = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  const handleMessageBubbleClick = useCallback((message: Message) => {
    if (suppressReplyClickRef.current) {
      suppressReplyClickRef.current = false;
      return;
    }
    handleReplyToMessage(message);
  }, [handleReplyToMessage]);

  const handleSendReaction = useCallback(async (message: Message, emoji: string) => {
    if (!selectedContact || !selectedSectorId) return;
    if (message.mediaType === 'system') return;
    const waId = message.whatsappMessageID;
    if (!waId) {
      window.alert('Esta mensagem ainda não tem ID do WhatsApp; não é possível reagir.');
      return;
    }
    try {
      await sendReaction({
        sectorId: Number(selectedSectorId),
        contactId: selectedContact.id,
        whatsappMessageId: waId,
        targetFromMe: message.isSent,
        emoji
      });
      const next =
        emoji && emoji.trim() !== ''
          ? emoji.trim()
          : undefined;
      setMessages(prev =>
        prev.map(m => {
          if (m.id === message.id) return { ...m, operatorReactionEmoji: next };
          if (waId && m.whatsappMessageID === waId) return { ...m, operatorReactionEmoji: next };
          return m;
        })
      );
      setMessageActionSheet(null);
    } catch (e) {
      console.error(e);
      window.alert('Não foi possível enviar a reação.');
    }
  }, [selectedContact, selectedSectorId]);

  const handleEmojiSelect = useCallback((emoji: { native?: string; unified?: string; skins?: Array<{ native: string }>; shortcodes?: string | string[]; id?: string }) => {
    const emojiChar = emojiMartToUnicode(emoji);
    if (!emojiChar) return;
    const el = messageInputRef.current;
    if (el) {
      const start = el.selectionStart ?? messageInput.length;
      const end = el.selectionEnd ?? messageInput.length;
      const newVal = messageInput.slice(0, start) + emojiChar + messageInput.slice(end);
      setMessageInput(newVal);
      setShowEmojiPicker(false);
      setTimeout(() => {
        el.focus();
        const pos = start + emojiChar.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setMessageInput(prev => prev + emojiChar);
      setShowEmojiPicker(false);
    }
  }, [messageInput]);

  // Função para formatar preview da resposta
  const formatReplyPreview = useCallback((message: Message) => {
    if (message.mediaType === 'image') return '🖼️ Imagem';
    if (message.mediaType === 'sticker') return '🎭 Figurinha';
    if (message.mediaType === 'gif') return 'GIF';
    if (message.mediaType === 'audio') return '🎵 Áudio';
    if (message.mediaType === 'document') return '📄 Documento';
    if (message.mediaType === 'video') return '🎥 Vídeo';
    return message.content || 'Mensagem';
  }, []);

  // Função para formatar mensagem de resposta
  const formatReplyMessage = useCallback((message: Message) => {
    const prefix = message.isSent ? '↗️' : '↙️';
    const mediaIcon = message.mediaType === 'image' ? '🖼️' :
      message.mediaType === 'sticker' ? '🎭' :
      message.mediaType === 'gif' ? 'GIF' :
      message.mediaType === 'audio' ? '🎵' :
        message.mediaType === 'document' ? '📄' :
          message.mediaType === 'video' ? '🎥' : '';

    if (message.mediaType !== 'text') {
      return `${prefix} ${mediaIcon} ${message.fileName || 'Arquivo'}`;
    }

    const content = message.content || '';
    const truncated = content.length > 40 ? content.substring(0, 40) + '...' : content;
    return `${prefix} ${truncated}`;
  }, []);

  // Carregar mensagens
  const loadMessages = useCallback(async (contactId: number, pageParam = 0, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) {
      setIsLoadingMore(true);
      isLoadingMoreRef.current = true;
    }

    const container = messagesContainerRef.current;
    if (append && container) {
      scrollRestoreRef.current = {
        prevScrollHeight: container.scrollHeight,
        prevScrollTop: container.scrollTop
      };
      if (DEBUG_SCROLL) console.log('[ChatMobile] loadMessages APPEND: guardando scroll', scrollRestoreRef.current);
    }

    try {
      const offset = pageParam * MESSAGES_PAGE_SIZE;
      const response = await getMessagesByContactIdWithPagination(contactId, MESSAGES_PAGE_SIZE, offset);
      const newMessages = response.messages || [];

      setHasMore(response.hasMore);

      if (append) {
        setPage(pageParam);
        setMessages(prev => {
          const updatedMessages = [
            ...newMessages.map(msg => ({
              ...msg,
              sentAt: normalizeSentAtToLocal(msg.sentAt)
            })),
            ...prev
          ].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());

          return updatedMessages;
        });
      } else {
        const sortedMessages = newMessages
          .map(msg => ({
            ...msg,
            sentAt: normalizeSentAtToLocal(msg.sentAt)
          }))
          .sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());

        setMessages(sortedMessages);

        if (sortedMessages.length > 0 && contactId) {
          const lastMsg = sortedMessages[sortedMessages.length - 1];
          const previewText = getMessagePreviewText(lastMsg);
          if (previewText) {
            setContacts(prev => prev.map(c =>
              Number(c.id) === contactId
                ? { ...c, lastMessage: previewText, lastMessageTime: lastMsg.sentAt }
                : c
            ));
          }
        }
      }
      setInitialLoad(false);
    } catch (error) {
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else if (pageParam === 0) {
        setTimeout(() => {
          setIsMessagesLoading(false);
          requestAnimationFrame(() => scrollToBottom());
        }, 50);
      }
      loadingRef.current = false;
    }
  }, [setMessages, setHasMore, scrollToBottom]);



  // Enviar arquivo
  const handleFileUpload = async (file: File) => {
    try {
      if (!selectedContact || !selectedSectorId) return;

      const contactId = selectedContact.id;
      const mediaType = file.type.startsWith('image/') ? 'image' : 'document';
      const caption = messageInput.trim();

      // Limpar mensagens não lidas do contato quando enviar arquivo
      if (selectedContact && setHasNewMessages) {
        setHasNewMessages((prev: { [contactId: number]: boolean }) => {
          const newState = { ...prev };
          delete newState[selectedContact.id];
          return newState;
        });
      }

      // Enviar arquivo - o WebSocket vai retornar a mensagem confirmada
      await sendFile({
        file,
        fileName: file.name,
        mediaType,
        caption,
        recipient: selectedContact.number,
        recipientPhone: selectedContact.number,
        contactId: selectedContact.id,
        sectorId: Number(selectedSectorId),
        isHuman: true,
        userId: currentUserId!,
        isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
        replyToMessageId: replyingTo?.whatsappMessageID || undefined
      });

      if (caption) {
        setMessageInput('');
      }

      console.log('Arquivo enviado com sucesso, aguardando WebSocket (Mobile)');

      // Limpar resposta após envio bem-sucedido
      if (replyingTo) {
        setReplyingTo(null);
        setReplyPreview('');
      }

      // Atualizar lista de contatos após envio
      if (selectedSectorId) {
        await markContactAsViewed(Number(selectedSectorId), contactId);

        // Dispatch event to notify CombinedMenu that this contact has been viewed
        window.dispatchEvent(new CustomEvent('contactViewed', {
          detail: { contactId: contactId }
        }));

        const includeArchived = false;
        const wasContactArchived = selectedContact.archived === true;
        if (wasContactArchived) {
          setShowArchivedContacts(false);
        }
        await loadContacts(Number(selectedSectorId), includeArchived, wasContactArchived ? false : showArchivedContacts);
        await reloadArchivedCount();
      }
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
    }
  };

  // Manipular seleção de arquivo (imagem ou documento)
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = '';
  };

  // Enviar áudio
  const handleSendAudio = async (audioBlob: Blob) => {
    if (!selectedContact || !selectedSectorId) return;

    setIsRecording(false);

    // Limpar mensagens não lidas do contato quando enviar áudio
    if (selectedContact && setHasNewMessages) {
      setHasNewMessages((prev: { [contactId: number]: boolean }) => {
        const newState = { ...prev };
        delete newState[selectedContact.id];
        return newState;
      });
    }

    try {
      // Enviar áudio - o WebSocket vai retornar a mensagem confirmada
      await sendAudioMessage(
        audioBlob,
        selectedContact.number,
        selectedContact.id,
        Number(selectedSectorId),
        currentUserId!,
        isCurrentUserAdmin ? isAnonymous : false,
        dayjs().format(),
        replyingTo?.whatsappMessageID || undefined
      );

      console.log('Áudio enviado com sucesso, aguardando WebSocket (Mobile)');

      // Limpar resposta após envio bem-sucedido
      if (replyingTo) {
        setReplyingTo(null);
        setReplyPreview('');
      }

      // Atualizar lista de contatos após envio
      if (selectedSectorId) {
        const includeArchived = false;
        const wasContactArchived = selectedContact.archived === true;
        if (wasContactArchived) {
          setShowArchivedContacts(false);
        }
        await loadContacts(Number(selectedSectorId), includeArchived, wasContactArchived ? false : showArchivedContacts);
        reloadArchivedCount();
      }
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
    }
  };

  // Envio de arquivo (imagem/documento) para grupo
  const handleGroupFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedGroup || !selectedSectorId) return;
    event.target.value = '';
    const isImage = file.type.startsWith('image/');
    const isAudio = ['audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/wav'].some(t => file.type.startsWith(t) || file.type === t);
    const base64 = await fileToBase64(file);
    const base64Clean = base64.includes(',') ? base64.split(',')[1]! : base64;
    const caption = messageInput.trim();
    try {
      if (isImage) {
        await sendGroupImage({
          sectorId: Number(selectedSectorId),
          groupId: selectedGroup.id,
          base64File: base64Clean,
          fileName: file.name,
          mediaType: file.type,
          caption,
          userId: currentUserId ?? undefined,
          isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
          replyToMessageId: replyingToGroupMessage?.whatsAppMessageId ?? undefined,
        });
      } else if (!isAudio) {
        await sendGroupDocument({
          sectorId: Number(selectedSectorId),
          groupId: selectedGroup.id,
          base64File: base64Clean,
          fileName: file.name,
          mediaType: file.type,
          caption,
          userId: currentUserId ?? undefined,
          isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
          replyToMessageId: replyingToGroupMessage?.whatsAppMessageId ?? undefined,
        });
      }
      if (caption) {
        setMessageInput('');
      }
      if (replyingToGroupMessage) {
        setReplyingToGroupMessage(null);
      }
      const list = await getGroupMessages(selectedGroup.id);
      setGroupMessages(list);
      setTimeout(() => scrollToBottomGroup(), 100);
    } catch (err) {
      console.error('Erro ao enviar arquivo no grupo:', err);
    }
  };

  // Envio de áudio para grupo
  const handleGroupSendAudio = async (audioBlob: Blob) => {
    if (!selectedGroup || !selectedSectorId) return;
    setIsRecording(false);
    setTimeout(() => scrollToBottomGroup(), 100);
    try {
      const base64 = await blobToBase64(audioBlob);
      const base64Clean = base64.includes(',') ? base64.split(',')[1]! : base64;
      await sendGroupAudioMessage({
        sectorId: Number(selectedSectorId),
        groupId: selectedGroup.id,
        base64File: base64Clean,
        fileName: `audio_${Date.now()}.wav`,
        userId: currentUserId ?? undefined,
        isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
        replyToMessageId: replyingToGroupMessage?.whatsAppMessageId ?? undefined,
      });
      if (replyingToGroupMessage) {
        setReplyingToGroupMessage(null);
      }
      const list = await getGroupMessages(selectedGroup.id);
      setGroupMessages(list);
      setTimeout(() => scrollToBottomGroup(), 100);
    } catch (err) {
      console.error('Erro ao enviar áudio no grupo:', err);
    }
  };

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedContact || !selectedSectorId) {
      return;
    }

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();
    const timezone = APP_TIMEZONE;

    // Guardar o texto da mensagem antes de limpar o input
    const messageText = messageInput;
    setMessageInput('');

    if (selectedContact) {
      updateHasNewMessages(prev => ({
        ...prev,
        [selectedContact.id]: false
      }));
    }

    const optimisticMessage: Message = {
      id: tempId,
      content: messageText,
      mediaType: 'text',
      mediaUrl: null,
      fileName: null,
      mimeType: null,
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending',
      replyToMessageID: replyingTo?.whatsappMessageID,
      repliedMessage: replyingTo ?? undefined,
    };
    setMessages(prev =>
      [...prev, optimisticMessage].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())
    );

    try {
      if (currentUserId === undefined) {
        throw new Error('Usuário não identificado');
      }

      const apiMsg = await sendMessage({
        text: messageText,
        to: selectedContact.number,
        recipientPhone: selectedContact.number,
        contactId: contactId,
        sectorId: Number(selectedSectorId),
        userId: currentUserId!,
        isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
        sentAt: sentAtString,
        timezone,
        replyToMessageId: replyingTo?.whatsappMessageID || undefined
      });

      setMessages(prev => {
        const idxByTemp = prev.findIndex(m => m.id === tempId);
        const idx =
          idxByTemp >= 0
            ? idxByTemp
            : prev.findIndex(
                m =>
                  m.status === 'sending' &&
                  m.mediaType === 'text' &&
                  (m.content || '').trim() === messageText.trim()
              );
        if (idx === -1) {
          console.warn('[Ligchat:ticks] send OK mas otimista não encontrado', { tempId });
          return prev;
        }
        return prev.map((m, i) =>
          i === idx
            ? {
                ...m,
                ...apiMsg,
                id: apiMsg.id ?? m.id,
                status: undefined,
                messageStatus: apiMsg.messageStatus || 'sent',
                whatsappMessageID: apiMsg.whatsappMessageID ?? m.whatsappMessageID
              }
            : m
        );
      });

      console.log('[Ligchat:ticks] API send OK, aguardando WS (Mobile)', { tempId });

      // Limpar resposta após envio bem-sucedido
      if (replyingTo) {
        setReplyingTo(null);
        setReplyPreview('');
      }

      // Atualizar lista de contatos após envio
      if (selectedSectorId) {
        const includeArchived = false;
        const wasContactArchived = selectedContact.archived === true;
        if (wasContactArchived) {
          setShowArchivedContacts(false);
        }
        await loadContacts(Number(selectedSectorId), includeArchived, wasContactArchived ? false : showArchivedContacts);
        // Também recarregar a contagem de arquivados
        await reloadArchivedCount();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
  };

  // Manipular tecla Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageInput.trim()) {
        if (selectedGroup) handleSendGroupMessage();
        else handleSendMessage();
      }
    }
  };

  const handleSendGroupMessage = async () => {
    if (!messageInput.trim() || !selectedGroup || !selectedSectorId) return;
    const text = messageInput;
    setMessageInput('');
    const pendingId = -Date.now();
    const pendingMsg: GroupMessageDto = {
      id: pendingId,
      grupoId: selectedGroup.id,
      senderName: 'Você',
      content: text,
      type: 'text',
      sentAt: dayjs().toISOString(),
      sentByUs: true,
    };
    setGroupMessages(prev =>
      [...prev, pendingMsg].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())
    );
    try {
      const { sentAt, message: savedMessage } = await sendGroupMessage({
        sectorId: Number(selectedSectorId),
        groupId: selectedGroup.id,
        message: text,
        userId: currentUserId ?? undefined,
        isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
        replyToMessageId: replyingToGroupMessage?.whatsAppMessageId ?? undefined,
      });
      if (replyingToGroupMessage) setReplyingToGroupMessage(null);
      const msgToAdd: GroupMessageDto = savedMessage ?? {
        id: -Date.now(),
        grupoId: selectedGroup.id,
        senderName: 'Você',
        content: text,
        type: 'text',
        sentAt: sentAt || dayjs().toISOString(),
        sentByUs: true,
      };
      setGroupMessages(prev => {
        const without = prev.filter(m => m.id !== pendingId);
        const next = [...without, msgToAdd].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
        return next;
      });
      setTimeout(() => scrollToBottomGroup(), 100);
    } catch (err) {
      console.error('Erro ao enviar mensagem no grupo:', err);
      setGroupMessages(prev => prev.filter(m => m.id !== pendingId));
    }
  };

  // Selecionar grupo e carregar mensagens
  const handleGroupSelect = async (group: GroupListDto) => {
    setSelectedGroup(group);
    setSelectedContact(null);
    setShowContactsList(false);
    setGroupMessages([]);
    setReplyingToGroupMessage(null);
    setMessageInput('');
    try {
      await markGroupAsViewed(group.id);
      const list = await getGroupMessages(group.id);
      setGroupMessages(list);
      setGroups(prev => prev.map(g => g.id === group.id ? { ...g, unreadCount: 0, isViewed: true } : g));
      setSelectedGroup(prev => prev?.id === group.id ? { ...prev, lastViewedAt: dayjs().toISOString() } : prev);
    } catch {
      setGroupMessages([]);
    }
  };

  // Excluir grupo apenas no Ligchat (não sai do grupo no WhatsApp)
  const handleExcluirGrupo = async () => {
    if (!selectedGroup || !selectedSectorId) return;
    if (!window.confirm('Excluir este grupo do Ligchat? Você continuará no grupo no WhatsApp; ele só deixará de aparecer aqui.')) return;
    try {
      await deleteGroupFromLigchat(selectedGroup.id);
      setGroups(prev => prev.filter(g => g.id !== selectedGroup.id));
      setSelectedGroup(null);
      setGroupMessages([]);
      setShowContactsList(true);
    } catch (err) {
      console.error('Erro ao excluir grupo:', err);
      window.alert('Não foi possível excluir o grupo.');
    }
  };

  // Selecionar contato
  const handleContactSelect = async (contact: ContactData) => {
    setSelectedGroup(null);
    setSelectedContact(contact);
    setShowContactsList(false);
    setPage(0);
    setInitialLoad(true);
    setMessages([]); // Limpar mensagens anteriores
    setIsMessagesLoading(true); // Mostrar loading

    // Limpar resposta ao mudar de contato
    setReplyingTo(null);
    setReplyPreview('');
    setMessageInput('');

    // Limpar indicador de nova mensagem localmente
    updateHasNewMessages(prev => ({
      ...prev,
      [contact.id]: false
    }));

    // NÃO limpar mensagens não lidas automaticamente ao selecionar contato
    // As mensagens não lidas só devem ser limpas quando o usuário responder

    // Marcar como visualizado ao abrir e só depois recarregar contatos
    if (selectedSectorId) {
      await markContactAsViewed(Number(selectedSectorId), contact.id);

      // Dispatch event to notify CombinedMenu that this contact has been viewed
      window.dispatchEvent(new CustomEvent('contactViewed', {
        detail: { contactId: contact.id }
      }));

      const includeArchived = false;
      await loadContacts(Number(selectedSectorId), includeArchived, showArchivedContacts);
    }

    // Carregar mensagens - o scroll será feito automaticamente na função loadMessages
    await loadMessages(contact.id, 0, false);
  };
  handleContactSelectRef.current = handleContactSelect;

  const resolveContactAfterConflict = async (digits: string): Promise<ContactData | null> => {
    if (!selectedSectorId) return null;
    const raw = await findContactByDigitsOnServer(Number(selectedSectorId), digits);
    return raw ? (normalizeContactLastMessage(raw) as ContactData) : null;
  };

  const handleContactShareChat = async (message: Message) => {
    if (!selectedSectorId) {
      window.alert('Selecione um setor no menu para abrir o contato.');
      return;
    }
    const { name, phoneRaw } = parseContactShareContent(message.content);
    const digits = formatPhoneForCreateContact(phoneRaw);
    if (!digits) {
      window.alert('Não foi possível ler um telefone válido neste cartão de contato.');
      return;
    }
    const sectorNum = Number(selectedSectorId);

    let resolved: ContactData | undefined =
      contacts.find((c) => contactMatchesPhone(c.number, digits));
    if (!resolved) {
      const remote = await findContactByDigitsOnServer(sectorNum, digits);
      if (remote) {
        resolved = normalizeContactLastMessage(remote) as ContactData;
      }
    }
    if (resolved) {
      await handleContactSelect(resolved);
      return;
    }

    setContactShareBusyMessageId(message.id);
    try {
      const created = await createContact({
        name: name.trim() || 'Contato',
        phoneNumber: digits,
        email: '',
        sectorId: sectorNum,
      });
      if (!created?.id) {
        window.alert('Não foi possível registrar o contato.');
        return;
      }

      await loadContacts(sectorNum, false, showArchivedContacts);

      let pick = await findContactByDigitsOnServer(sectorNum, digits);
      if (!pick) {
        await new Promise((r) => setTimeout(r, 450));
        pick = await findContactByDigitsOnServer(sectorNum, digits);
      }

      const toOpen = pick ? normalizeContactLastMessage(pick) : normalizeContactLastMessage(created);
      await handleContactSelect(toOpen as ContactData);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        await loadContacts(sectorNum, false, showArchivedContacts);
        let pick = await findContactByDigitsOnServer(sectorNum, digits);
        if (!pick) {
          await new Promise((r) => setTimeout(r, 450));
          pick = await findContactByDigitsOnServer(sectorNum, digits);
        }
        const found = pick ? (normalizeContactLastMessage(pick) as ContactData) : await resolveContactAfterConflict(digits);
        if (found) {
          await handleContactSelect(found);
          return;
        }
      }
      const msg = e?.response?.data?.message || e?.message || 'Erro ao criar contato.';
      window.alert(typeof msg === 'string' ? msg : 'Erro ao criar contato.');
    } finally {
      setContactShareBusyMessageId(null);
    }
  };

  // Voltar para lista de contatos/grupos
  const handleBackToContacts = () => {
    setSelectedContact(null);
    setSelectedGroup(null);
    setShowContactsList(true);
    setMessages([]);
    setGroupMessages([]);

    // Limpar resposta ao voltar para lista de contatos
    setReplyingTo(null);
    setReplyPreview('');
    setReplyingToGroupMessage(null);
    setMessageInput('');
  };

  // Renderizar mensagem
  const renderMessage = (message: Message) => {
    const isImage = message.mediaType === 'image';
    const isDocument = message.mediaType === 'document';
    const isAudio = message.mediaType === 'audio' || message.mediaType === 'voice';
    const isVideo = message.mediaType === 'video';
    const isSystem = message.mediaType === 'system';
    const isSticker = message.mediaType === 'sticker';
    const isGif = message.mediaType === 'gif';
    const isContact = message.mediaType === 'contact';
    const isInteractive = message.mediaType === 'interactive';

    // Renderizar mensagem de sistema (transferência, etc) com estilo especial
    if (isSystem) {
      // Remover emojis do conteúdo (método simples)
      const cleanContent = (message.content || '').replace('📋', '').replace('✅', '').replace('❌', '').trim();
      return (
        <div
          key={message.id}
          id={`message-${message.id}`}
          className="cmob-system-message"
        >
          <span>{cleanContent}</span>
        </div>
      );
    }

    // Determinar se deve mostrar o nome do remetente
    const showSenderName = message.isSent && !message.isAnonymous && message.senderName;

    return (
      <div
        key={message.id}
        id={`message-${message.id}`}
        className={`cmob-message ${message.isSent ? 'sent' : 'received'} ${replyingTo?.id === message.id ? 'replying-to' : ''} ${message.replyToMessageID ? 'reply' : ''} ${isContact ? 'cmob-message--contact-card' : ''}`}
        onClick={() => handleMessageBubbleClick(message)}
        onPointerDown={() => handleMessagePointerDown(message)}
        onPointerUp={handleMessagePointerEnd}
        onPointerCancel={handleMessagePointerEnd}
        onPointerLeave={handleMessagePointerEnd}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleReplyToMessage(message)}
        aria-label="Toque para responder; segure para reagir ou responder"
      >
        <div className="cmob-message-content">
          {message.isForwarded && (
            <ForwardedMessageHeader variant="mobile" isSent={!!message.isSent} />
          )}
          {/* Mostrar mensagem respondida se existir */}
          {message.replyToMessageID && (
            <div className="cmob-reply-to-message">
              <div className="cmob-reply-indicator">
                <span className="cmob-reply-text">Respondendo a:</span>
              </div>
              {message.repliedMessage && (
                <div
                  className="cmob-original-message"
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToRepliedMessage(message.repliedMessage!.id);
                  }}
                >
                  <div className="cmob-original-content">
                    {message.repliedMessage.mediaType === 'text' || !message.repliedMessage.mediaType
                      ? formatWhatsAppText(message.repliedMessage.content || '')
                      : (getMessagePreviewText(message.repliedMessage) || 'Mensagem')}
                  </div>
                  <div className="cmob-original-time">
                    {formatMessageTime(message.repliedMessage.sentAt)}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Mostrar nome do remetente se não for anônimo */}
          {showSenderName && (
            <div className="cmob-message-sender">
              <strong>{message.senderName ?? ''}:</strong>
            </div>
          )}
          {isImage && getMessageMediaUrl(message) && (() => {
            const imgCaption = (message.content || '').trim();
            const imgInner = (
              <div className="cmob-message-image">
                <img
                  src={getMessageMediaUrl(message)!}
                  alt="Imagem"
                  onLoad={() => scrollToBottomIfNearBottom()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentModal({
                      url: getMessageMediaUrl(message)!,
                      fileName: message.fileName || 'Imagem',
                      type: 'image',
                      message
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            );
            if (!imgCaption) return imgInner;
            return (
              <div className="cmob-message-media-stack has-caption">
                {imgInner}
                <div className="cmob-message-media-caption">{formatWhatsAppText(message.content || '')}</div>
              </div>
            );
          })()}
          {isSticker && getMessageMediaUrl(message) && (
            <div className="cmob-message-sticker">
              <img
                src={getMessageMediaUrl(message)!}
                alt="Figurinha"
                onLoad={() => scrollToBottomIfNearBottom()}
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentModal({
                    url: getMessageMediaUrl(message)!,
                    fileName: 'Figurinha',
                    type: 'image',
                    message
                  });
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          )}
          {isGif && getMessageMediaUrl(message) && (
            <div className="cmob-message-gif">
              <img
                src={getMessageMediaUrl(message)!}
                alt="GIF"
                onLoad={() => scrollToBottomIfNearBottom()}
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentModal({
                    url: getMessageMediaUrl(message)!,
                    fileName: 'GIF',
                    type: 'image',
                    message
                  });
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          )}
          {isDocument && (() => {
            const mediaUrl = getMessageMediaUrl(message);
            const docCaption = (message.content || '').trim();
            const fileLabel = message.fileName || docCaption || 'Documento';
            const showCaption = docCaption !== '' && docCaption !== fileLabel;
            const docRow = mediaUrl ? (
              <div
                className="cmob-message-document"
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentModal({
                    url: mediaUrl,
                    fileName: message.fileName || 'documento',
                    type: 'document',
                    message
                  });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setAttachmentModal({
                  url: mediaUrl,
                  fileName: message.fileName || 'documento',
                  type: 'document',
                  message
                })}
              >
                <DocumentIcon />
                <span>{fileLabel}</span>
              </div>
            ) : (
              <div className="cmob-message-document">
                <DocumentIcon />
                <span>{fileLabel}</span>
              </div>
            );
            if (!showCaption) return docRow;
            return (
              <div className="cmob-message-media-stack has-caption">
                {docRow}
                <div className="cmob-message-media-caption">{formatWhatsAppText(message.content || '')}</div>
              </div>
            );
          })()}
          {isAudio && (
            getMessageMediaUrl(message) ? (
              <div className="cmob-message-audio">
                <AudioMessage
                  src={getMessageMediaUrl(message)!}
                  isSent={message.isSent}
                  onCancel={message.status === 'sending' ? () => {
                    setMessages(prev => prev.filter(m => m.id !== message.id));
                  } : undefined}
                />
              </div>
            ) : (
              <div className="cmob-message-audio cmob-message-audio--placeholder">
                <span>🎵 Áudio</span>
              </div>
            )
          )}
          {isVideo && (() => {
            const vidCaption = (message.content || '').trim();
            const vidInner = (
              <div
                className="cmob-message-video"
                onClick={(e) => {
                  e.stopPropagation();
                  getMessageMediaUrl(message) && window.open(getMessageMediaUrl(message)!, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                <video controls onLoadedData={() => scrollToBottomIfNearBottom()}>
                  <source src={getMessageMediaUrl(message) || ''} type={message.mimeType || 'video/mp4'} />
                </video>
              </div>
            );
            if (!vidCaption) return vidInner;
            return (
              <div className="cmob-message-media-stack has-caption">
                {vidInner}
                <div className="cmob-message-media-caption">{formatWhatsAppText(message.content || '')}</div>
              </div>
            );
          })()}
          {isContact && (() => {
            const parsed = parseContactShareContent(message.content);
            return (
              <ContactShareCard
                name={parsed.name}
                phoneLabel={parsed.phoneRaw}
                timeLabel={formatMessageTime(message.sentAt)}
                isSent={!!message.isSent}
                chatBusy={contactShareBusyMessageId === message.id}
                onChat={(e) => {
                  e.stopPropagation();
                  void handleContactShareChat(message);
                }}
              />
            );
          })()}
          {isInteractive && selectedContact && currentUserId != null && (
            <InteractiveMessageBubble
              content={message.content}
              isSent={!!message.isSent}
              recipientPhone={selectedContact.number}
              sectorId={Number(selectedSectorId)}
              userId={currentUserId}
              isAnonymous={isCurrentUserAdmin ? isAnonymous : false}
            />
          )}
          {!isInteractive && !isImage && !isDocument && !isAudio && !isVideo && message.mediaType !== 'contact' && !isSticker && !isGif && (
            <span>{formatWhatsAppText(message.content || '')}</span>
          )}
          {message.reactionEmoji ? (
            <span className="cmob-message-reaction-badge" aria-label="Reação">
              {message.reactionEmoji}
            </span>
          ) : null}
          {message.operatorReactionEmoji ? (
            <span className="cmob-message-reaction-badge cmob-message-reaction-badge-operator" aria-label="Sua reação">
              {message.operatorReactionEmoji}
            </span>
          ) : null}
        </div>
        {!(isContact && !message.isSent) && (
          <div className="cmob-message-info">
            {!isContact && (
              <span className="cmob-message-time">
                {formatMessageTime(message.sentAt)}
              </span>
            )}
            {message.isSent && (
              <span className={`cmob-message-status ${message.messageStatus || message.status || 'sent'}`}>
                {message.status === 'sending' && '✓'}
                {message.status === 'error' && '❌'}
                {message.status !== 'sending' && message.status !== 'error' && message.messageStatus === 'read' && (
                  <span className="status-read">✓✓</span>
                )}
                {message.status !== 'sending' && message.status !== 'error' &&
                  (message.messageStatus === 'delivered' || message.messageStatus === 'received') &&
                  '✓✓'}
                {message.status !== 'sending' && message.status !== 'error' && (!message.messageStatus || message.messageStatus === 'sent') && '✓✓'}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filtrar contatos
  const filteredContacts = React.useMemo(() => {
    const q = searchTerm.trim();
    const hasSearch = q.length > 0;

    return contacts.filter(contact => {
      if (!contact || !contact.name) {
        return false;
      }

      if (!hasSearch) {
        if (showPendingContacts) {
          if (contact.contactStatus !== 'Aguardando atendimento') return false;
          if (contact.archived === true) return false;
        } else if (showRecentContacts) {
          if (contact.archived === true) return false;
          const lastTime = contact.lastMessageTime || (contact as any).last_message_time || (contact as any).updatedAt || contact.createdAt;
          if (!lastTime) return false;
          const threeDaysAgo = dayjs().subtract(3, 'day');
          if (!dayjs(lastTime).isAfter(threeDaysAgo)) return false;
        } else if (showArchivedContacts) {
          if (contact.archived !== true) return false;
        }
        if (!showArchivedContacts && !showRecentContacts && !showPendingContacts && contact.archived === true) return false;
      } else {
        if (showArchivedContacts) {
          if (contact.archived !== true) return false;
        } else if (contact.archived === true) {
          return false;
        }
      }

      const matchesSearch = !hasSearch || (
        contact.name.toLowerCase().includes(q.toLowerCase()) ||
        (contact.number && contact.number.includes(q)) ||
        (contact.email && contact.email.toLowerCase().includes(q.toLowerCase()))
      );

      const matchesUser = selectedFilterUser === null || contact.assignedTo === selectedFilterUser;

      const matchesTag = selectedFilterTag === null || contact.tagId === selectedFilterTag;

      const matchesStatus = !selectedStatus || selectedStatus === 'all' ||
        (selectedStatus === 'active' && contact.isActive !== false) ||
        (selectedStatus === 'inactive' && contact.isActive === false) ||
        contact.contactStatus === selectedStatus;

      return matchesSearch && matchesUser && matchesTag && matchesStatus;
    });
  }, [contacts, searchTerm, selectedFilterUser, selectedFilterTag, selectedStatus, showArchivedContacts, showRecentContacts, showPendingContacts]);

  /**
   * Resolver de menções (@<digits>) → nome amigável.
   * Prioridade: histórico de remetentes do grupo (senderJid → senderName) > contatos do setor (number).
   * Retorna undefined quando não encontra; o formatter cai no fallback de telefone formatado.
   */
  const groupMentionResolver = useMemo(() => {
    const map = new Map<string, string>();
    const onlyDigits = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '');
    const last9 = (d: string) => (d.length >= 9 ? d.slice(-9) : d);
    for (const gm of groupMessages) {
      const d = onlyDigits(gm.senderJid);
      const name = (gm.senderName ?? '').trim();
      if (d && name && !map.has(d)) {
        map.set(d, name);
        const tail = last9(d);
        if (tail !== d && !map.has(tail)) map.set(tail, name);
      }
    }
    for (const c of contacts) {
      const d = onlyDigits(c.number);
      const name = (c.name ?? '').trim();
      if (d && name && !map.has(d)) {
        map.set(d, name);
        const tail = last9(d);
        if (tail !== d && !map.has(tail)) map.set(tail, name);
      }
    }
    return (digits: string): string | undefined => {
      if (map.has(digits)) return map.get(digits);
      const tail = last9(digits);
      return map.get(tail);
    };
  }, [groupMessages, contacts]);

  // Função para alternar IA/Humano no contato
  const handleToggleAIActive = async () => {
    if (!selectedContact) return;
    const newValue = !selectedContact.aiActive;
    try {
      await setContactAIActive(selectedContact.id, newValue);
      setSelectedContact(prev => prev ? { ...prev, aiActive: newValue } : null);
      setContacts(prev => prev.map(c =>
        c.id === selectedContact.id ? { ...c, aiActive: newValue } : c
      ));
    } catch (error) {
      console.error('[ChatMobile] Erro ao alternar IA:', error);
    }
  };

  // Função para concluir atendimento (reativar IA e limpar status)
  const handleConcludeAttendance = async () => {
    if (!selectedContact) return;

    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedContact.tagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: true,
        contactStatus: '',
        assignedTo: selectedContact.assignedTo
      });

      setSelectedContact(prev => prev ? { ...prev, aiActive: true, contactStatus: '' } : null);
      setContacts(prev => prev.map(c =>
        c.id === selectedContact.id
          ? { ...c, aiActive: true, contactStatus: '' }
          : c
      ));

      setShowHeaderMenu(false);

      if (selectedSectorId) {
        try {
          const userName = currentUserName || 'Atendente';
          await sendSystemMessage(
            selectedContact.id,
            Number(selectedSectorId),
            `✅ Atendimento concluído por ${userName}. IA reativada.`
          );
        } catch (e) {
          console.error('[ChatMobile] Erro ao enviar mensagem de conclusão:', e);
        }
      }
    } catch (error) {
      console.error('[ChatMobile] Erro ao concluir atendimento:', error);
    }
  };

  // Função para clicar na notificação e abrir o chat
  const handleNotificationClick = useCallback(async (contactId: number) => {
    removeByContactId(contactId);

    if (showArchivedContacts) {
      setShowArchivedContacts(false);
    }
    setShowGroupsView(false);
    setShowRecentContacts(false);
    setShowPendingContacts(true);

    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      handleContactSelect(contact);
      setShowContactsList(false);
    } else if (selectedSectorId) {
      await loadContacts(Number(selectedSectorId), false, false);
      setContacts(prev => {
        const found = prev.find(c => c.id === contactId);
        if (found) {
          setTimeout(() => {
            handleContactSelect(found);
            setShowContactsList(false);
          }, 100);
        }
        return prev;
      });
    }
  }, [contacts, showArchivedContacts, showPendingContacts, selectedSectorId, removeByContactId]);

  // Listener para evento selectContact (disparado pelo sino de notificação no menu)
  useEffect(() => {
    const handleSelectContactEvent = (event: CustomEvent) => {
      const { contactId } = event.detail;
      if (contactId) {
        handleNotificationClick(contactId);
      }
    };

    window.addEventListener('selectContact', handleSelectContactEvent as EventListener);
    return () => {
      window.removeEventListener('selectContact', handleSelectContactEvent as EventListener);
    };
  }, [handleNotificationClick]);

  // Função para transferir contato
  const handleTransferContact = async (newUserId?: number | null) => {
    if (!selectedContact) return;
    
    const userIdToUse = newUserId !== undefined ? newUserId : selectedUserId;
    if (userIdToUse === null) return;
    if (userIdToUse === selectedContact.assignedTo) return;

    setIsTransferLoading(true);
    const finalUserId = userIdToUse === -1 ? null : userIdToUse;
    const finalUserName = userIdToUse === -1 ? null : (users.find(u => u.id === userIdToUse)?.name || null);
    const previousResponsible = selectedContact.responsibleName || 'Nenhum';

    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedContact.tagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: finalUserId
      });

      let transferMessage = '';
      if (userIdToUse === -1) {
        transferMessage = `📋 Responsável removido do contato. (Antes: ${previousResponsible})`;
      } else if (userIdToUse === currentUserId) {
        transferMessage = `📋 Você assumiu o contato.`;
      } else {
        transferMessage = `📋 Contato transferido de ${previousResponsible} para ${finalUserName || 'outro usuário'}.`;
      }

      const systemMsg = await sendSystemMessage(
        selectedContact.id,
        selectedContact.sectorId,
        transferMessage
      );

      const newSystemMessage = {
        id: systemMsg.id,
        content: systemMsg.content,
        mediaType: 'system',
        mediaUrl: null,
        fileName: null,
        mimeType: null,
        sectorId: selectedContact.sectorId,
        contactID: selectedContact.id,
        sentAt: systemMsg.sentAt,
        isSent: true,
        isRead: true
      };

      setMessages(prev => {
        let newSentAt = dayjs().valueOf();
        if (prev.length > 0) {
          const lastMsg = prev[prev.length - 1];
          const lastTs = dayjs(lastMsg.sentAt).valueOf();
          if (lastTs >= newSentAt) {
            newSentAt = lastTs + 1;
          }
        }
        const finalSentAtString = dayjs(newSentAt).format();

        const newSystemMessageWithNormalizedTime = {
          ...newSystemMessage,
          sentAt: finalSentAtString
        };

        const updated = [...prev, newSystemMessageWithNormalizedTime];
        return updated.sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
      });

      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, assignedTo: finalUserId, responsibleName: finalUserName }
          : contact
      ));

      if (selectedContact) {
        setSelectedContact(prev => prev ? { ...prev, assignedTo: finalUserId, responsibleName: finalUserName } : null);
      }

      // Notificar via WebSocket o usuário que recebeu a transferência (para exibir no sino)
      if (finalUserId !== null && finalUserId !== currentUserId && finalUserName) {
        notifyContactResponsible(selectedContact.id, selectedContact.sectorId, finalUserId, finalUserName, selectedContact.name);
      }

      setShowTransferModal(false);
      setSelectedUserId(null);
      setShowHeaderMenu(false);
    } catch (error) {
    } finally {
      setIsTransferLoading(false);
    }
  };

  // Função para alterar tag
  const handleUpdateTag = async (newTagId?: number | null) => {
    if (!selectedContact) return;
    
    const tagIdToUse = newTagId !== undefined ? newTagId : selectedTagId;
    if (tagIdToUse === selectedContact.tagId) return;

    setIsTagLoading(true);
    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: tagIdToUse,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedContact.assignedTo
      });

      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, tagId: tagIdToUse }
          : contact
      ));

      setSelectedContact(prev => prev ? { ...prev, tagId: tagIdToUse } : null);
      setShowTagModal(false);
      setSelectedTagId(null);
      setShowHeaderMenu(false);
    } catch (error) {
    } finally {
      setIsTagLoading(false);
    }
  };

  // Função para excluir contato
  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setIsDeletingContact(true);
    try {
      await deleteContact(selectedContact.id);

      // Remover o contato da lista
      setContacts(prev => prev.filter(contact => contact.id !== selectedContact.id));

      // Voltar para a lista de contatos
      handleBackToContacts();
      setShowDeleteModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
    } finally {
      setIsDeletingContact(false);
    }
  };

  // Função para arquivar/desarquivar contato
  const handleArchiveContact = async () => {
    if (!selectedContact) return;

    setIsArchivingContact(true);
    try {
      const isCurrentlyArchived = selectedContact.archived || false;
      await archiveContact(selectedContact.id, !isCurrentlyArchived);

      // Remover o contato da lista atual (já que mudou de status)
      setContacts(prev => prev.filter(c => c.id !== selectedContact.id));

      // Atualizar contagem de arquivados imediatamente (otimista)
      setArchivedContactsCount(prev => Math.max(0, prev + (isCurrentlyArchived ? -1 : 1)));
      // Reconciliar com o backend em segundo plano
      reloadArchivedCount();

      // Voltar para a lista de contatos
      handleBackToContacts();
      setShowArchiveModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
    } finally {
      setIsArchivingContact(false);
    }
  };

  // Função para criar novo contato
  const handleCreateContact = async () => {
    if (!newContactName.trim() || !newContactNumber.trim() || !selectedSectorId) {
      setNewContactError('Nome e número são obrigatórios');
      return;
    }

    setIsCreatingContact(true);
    setNewContactError('');

    try {
      const newContact = await createContact({
        name: newContactName.trim(),
        phoneNumber: newContactNumber.trim(),
        email: '',
        sectorId: Number(selectedSectorId)
      });

      // Adicionar o novo contato à lista
      if (newContact) {
        setContacts(prev => {
          const updated = [newContact, ...prev];
          return updated;
        });

        // Limpar o formulário e fechar modal
        setNewContactName('');
        setNewContactNumber('');
        setShowNewContactModal(false);
      } else {
        setNewContactError('Erro ao criar contato. Tente novamente.');
      }
    } catch (error: any) {

      // Tratar diferentes tipos de erro
      if (error?.response?.status === 409) {
        setNewContactError('Já existe um contato com este número de telefone neste setor');
      } else if (error?.response?.data?.message) {
        setNewContactError(error.response.data.message);
      } else {
        setNewContactError('Erro ao criar contato. Tente novamente.');
      }
    } finally {
      setIsCreatingContact(false);
    }
  };

  const handleForwardContact = async () => {
    if (!selectedContact || !selectedContactToForward || !selectedSectorId) return;

    setIsForwardingContact(true);
    try {
      const isOfficial = SessionService.getSectorIsOfficial();
      // Enviar o contato ESCOLHIDO (selectedContactToForward) para o chat ATUAL (selectedContact)
      const recipientNumber = selectedContact.number;
      const contactToSendName = selectedContactToForward.name;
      const contactToSendPhone = selectedContactToForward.number;
      const contactToSendId = selectedContactToForward.id;

      if (!isOfficial) {
        const { sendContact } = await import('../services/UnofficialMessageService');
        
        const response = await sendContact({
          recipient: recipientNumber,
          sectorId: Number(selectedSectorId),
          contactName: contactToSendName,
          contactPhone: contactToSendPhone,
          userId: currentUserId || undefined,
          isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
          sentAt: dayjs().format(),
          timezone: APP_TIMEZONE
        });

        const newMessage: Message = {
          id: Date.now(),
          content: `📇 ${contactToSendName}\n📞 ${formatPhoneNumber(contactToSendPhone)}`,
          mediaType: 'contact',
          mediaUrl: null,
          fileName: `${contactToSendName}.vcf`,
          mimeType: 'text/vcard',
          sectorId: Number(selectedSectorId),
          contactID: selectedContact.id,
          sentAt: response.timestamp || dayjs().format(),
          isSent: true,
          isRead: false,
          status: 'sent',
          whatsappMessageID: response.data?.whatsappMessageID
        };

        setMessages(prev => [...prev, newMessage].sort((a, b) => 
          dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf()
        ));
      } else {
        const { sendContact: sendContactOfficial } = await import('../services/ContactShareService');
        
        await sendContactOfficial({
          recipientPhone: recipientNumber,
          contactId: contactToSendId,
          sectorId: Number(selectedSectorId),
          contactName: contactToSendName,
          contactPhone: contactToSendPhone,
          userId: currentUserId || undefined,
          isAnonymous: isCurrentUserAdmin ? isAnonymous : false,
          sentAt: dayjs().format(),
          timezone: APP_TIMEZONE
        });

        const newMessage: Message = {
          id: Date.now(),
          content: `📇 ${contactToSendName}\n📞 ${formatPhoneNumber(contactToSendPhone)}`,
          mediaType: 'contact',
          mediaUrl: null,
          fileName: `${contactToSendName}.vcf`,
          mimeType: 'text/vcard',
          sectorId: Number(selectedSectorId),
          contactID: selectedContact.id,
          sentAt: dayjs().format(),
          isSent: true,
          isRead: false,
          status: 'sent'
        };

        setMessages(prev => [...prev, newMessage].sort((a, b) => 
          dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf()
        ));
      }

      setShowForwardContactModal(false);
      setSelectedContactToForward(null);
      setForwardContactSearch('');
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('[ChatMobile] Erro ao encaminhar contato:', error);
      alert('Erro ao encaminhar contato. Tente novamente.');
    } finally {
      setIsForwardingContact(false);
    }
  };

  // Função para salvar o nome editado
  const handleSaveName = async () => {
    if (!selectedContact || !editingName.trim()) return;

    setIsSavingName(true);
    try {
      await updateContact(selectedContact.id, {
        name: editingName.trim(),
        tagId: selectedContact.tagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedContact.assignedTo
      });

      // Atualizar o contato na lista
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, name: editingName.trim() }
          : contact
      ));

      setSelectedContact(prev => prev ? { ...prev, name: editingName.trim() } : null);
      setShowNameEditModal(false);
      setEditingName('');
    } catch (error) {
    } finally {
      setIsSavingName(false);
    }
  };

  // Função para cancelar a edição do nome
  const handleCancelNameEdit = () => {
    setShowNameEditModal(false);
    setEditingName('');
  };

  // Função para iniciar a edição do nome
  const handleStartNameEdit = () => {
    setEditingName(selectedContact?.name || '');
    setShowNameEditModal(true);
  };

  const handleImageClick = (imageUrl: string, imageAlt?: string) => {
    setSelectedImageUrl(imageUrl);
    setSelectedImageAlt(imageAlt || 'Imagem');
    setShowImageViewer(true);
  };

  const openAvatarViewer = (url: string, alt: string) => {
    setAvatarViewerUrl(url);
    setAvatarViewerAlt(alt || 'Foto');
    setShowAvatarViewer(true);
  };

  // Função para verificar se pode excluir contato
  const canDeleteContact = () => {
    if (!selectedContact || !isCurrentUserAdmin) return false;

    // Admin pode excluir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Usuário comum só pode excluir se for o responsável
    return selectedContact.assignedTo === currentUserId;
  };

  // Função para verificar se pode transferir contato
  const canTransferContact = () => {
    if (!selectedContact) return false;

    // Admin pode transferir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Colaborador pode transferir se:
    // 1. O contato está atribuído a ele (assignedTo === currentUserId)
    // 2. O contato não tem responsável (assignedTo é null ou undefined)
    return selectedContact.assignedTo === currentUserId ||
      selectedContact.assignedTo === null ||
      selectedContact.assignedTo === undefined;
  };

  // Função para recarregar a contagem de arquivados
  const reloadArchivedCount = async () => {
    if (!selectedSectorId) return;
    try {

      const allContactsData = await getContactsWithFilters(Number(selectedSectorId), false, true);

      const allContactsList = Array.isArray(allContactsData) ? allContactsData : (allContactsData as any)?.data || [];

      // Verificar cada contato individualmente
      // logs removidos

      const archived = allContactsList.filter((c: any) => c.archived).length;

      setArchivedContactsCount(archived);
    } catch (error) {
    }
  };

  // Função para carregar contatos
  const loadContacts = async (sectorId: number, includeArchived: boolean = false, onlyArchived: boolean = false) => {
    try {
      const contactsData = await getContactsWithFilters(sectorId, includeArchived, onlyArchived);
      const rawList = Array.isArray(contactsData) ? contactsData : (contactsData as any)?.data || [];

      setContacts(prev => {
        const normalized = rawList.map((c: any) => normalizeContactLastMessage(c));
        return normalized.map((c: any) => {
          const existing = prev.find(p => Number(p.id) === Number(c.id));
          if (existing?.lastMessage && !c.lastMessage) {
            return { ...c, lastMessage: existing.lastMessage, lastMessageTime: existing.lastMessageTime ?? c.lastMessageTime };
          }
          return c;
        });
      });

      return contactsData;
    } catch (error) {
      // Fallback para a função antiga
      try {
        const fallbackData = await getContacts(sectorId);
        if (Array.isArray(fallbackData)) {
          setContacts(fallbackData);
        } else if (fallbackData && 'data' in fallbackData) {
          setContacts((fallbackData as any).data || []);
        }
        return fallbackData;
      } catch (fallbackError) {
        setContacts([]);
        return [];
      }
    }
  };

  // Abrir conversa ao vir do CRM (location.state ou sessionStorage)
  useEffect(() => {
    const state = location.state as { contactId?: number } | null;
    const storageRaw = sessionStorage.getItem('selectedContactId');
    const contactId = state?.contactId ?? (storageRaw ? Number(storageRaw) : NaN);
    if (!Number.isFinite(contactId) || contactId <= 0) return;

    sessionStorage.removeItem('selectedContactId');
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });

    const sectorId = SessionService.getSectorId();
    if (!sectorId) return;

    (async () => {
      try {
        const data = await getContactsWithFilters(Number(sectorId), false, false);
        const rawList = Array.isArray(data) ? data : (data as any)?.data || [];
        const c = rawList
          .map((raw: any) => normalizeContactLastMessage(raw))
          .find((x: ContactData) => Number(x.id) === Number(contactId));
        if (c) {
          setShowGroupsView(false);
          setShowRecentContacts(false);
          setShowPendingContacts(false);
          setShowArchivedContacts(false);
          setShowContactsList(false);
          await handleContactSelectRef.current(c);
        }
      } catch (e) {
        console.error('[ChatMobile] Erro ao abrir contato a partir do CRM:', e);
      }
    })();
  }, [location.key, location.pathname, location.search, navigate]);

  const loadGroups = useCallback(async (sectorId: number) => {
    try {
      const list = await getGroupsBySector(sectorId);
      const normalized = (Array.isArray(list) ? list : []).map((g: GroupListDto) => ({
        ...g,
        unreadCount: typeof g.unreadCount === 'number' ? g.unreadCount : 0,
      }));
      setGroups(normalized);
      // Atualizar grupo selecionado com dados frescos (foto, nome, etc.) quando a lista for recarregada
      setSelectedGroup(prev => {
        if (!prev) return prev;
        const updated = normalized.find((g: GroupListDto) => g.id === prev.id);
        return updated ? { ...prev, ...updated } : prev;
      });
    } catch {
      setGroups([]);
    }
  }, []);

  // Inicialização
  useEffect(() => {
    const initializeData = async () => {
      try {
        const sectorId = SessionService.getSectorId();
        if (sectorId) {
          setSelectedSectorId(String(sectorId));

          // Carregar dados
          const [contactsData, usersData, tagsData, allContactsData] = await Promise.all([
            loadContacts(Number(sectorId), false, false),
            getAllUsers(undefined, Number(sectorId)),
            getTags(Number(sectorId)),
            getContactsWithFilters(Number(sectorId), false, true), // Carregar apenas arquivados para contar
            loadGroups(Number(sectorId))
          ]);

          // Processar dados dos usuários
          if (Array.isArray(usersData)) {
            setUsers(usersData);
          } else if (usersData && 'data' in usersData) {
            setUsers((usersData as any).data || []);
          }

          // Processar dados das tags
          if (Array.isArray(tagsData)) {
            setTags(tagsData);
          } else if (tagsData && 'data' in tagsData) {
            setTags((tagsData as any).data || []);
          }

          // Contar arquivados
          const allContactsList = Array.isArray(allContactsData) ? allContactsData : (allContactsData as any)?.data || [];

          // Verificar cada contato individualmente
          // logs removidos

          const archived = allContactsList.filter((c: any) => c.archived).length;
          setArchivedContactsCount(archived);
        }

        // Dados do usuário já estão sendo obtidos da sessão na inicialização dos estados

      } catch (error) {
      }
    };

    initializeData();
  }, []);

  // Listener para mudança de setor no sidemenu - recarrega leads e dados
  useEffect(() => {
    const handleSectorChange = async (event: Event) => {
      const { sectorId } = (event as CustomEvent).detail;
      if (sectorId) {
        setSelectedSectorId(String(sectorId));
        setSelectedContact(null);
        setShowHeaderMenu(false);
        try {
          const [contactsData, usersData, tagsData, archivedContactsData] = await Promise.all([
            loadContacts(Number(sectorId), false, false),
            getAllUsers(undefined, Number(sectorId)),
            getTags(Number(sectorId)),
            getContactsWithFilters(Number(sectorId), false, true)
          ]);
          if (Array.isArray(contactsData)) {
            setContacts(contactsData);
          } else if (contactsData && 'data' in contactsData) {
            setContacts((contactsData as any).data || []);
          }
          if (Array.isArray(usersData)) {
            setUsers(usersData);
          } else if (usersData && 'data' in usersData) {
            setUsers((usersData as any).data || []);
          }
          if (Array.isArray(tagsData)) {
            setTags(tagsData);
          } else if (tagsData && 'data' in tagsData) {
            setTags((tagsData as any).data || []);
          }
          const archivedList = Array.isArray(archivedContactsData) ? archivedContactsData : (archivedContactsData as any)?.data || [];
          setArchivedContactsCount(archivedList.length);
        } catch (err) {
          setContacts([]);
          setUsers([]);
          setTags([]);
          setArchivedContactsCount(0);
        }
      } else {
        setSelectedSectorId('');
        setSelectedContact(null);
        setContacts([]);
        setUsers([]);
        setTags([]);
        setArchivedContactsCount(0);
      }
    };

    window.addEventListener('sectorChanged', handleSectorChange);
    return () => {
      window.removeEventListener('sectorChanged', handleSectorChange);
    };
  }, []);

  // Recarregar contatos quando o filtro de arquivados/aguardando mudar
  useEffect(() => {
    const reloadContacts = async () => {
      const sectorId = SessionService.getSectorId();
      if (sectorId) {
        const includeArchived = false;
        await loadContacts(Number(sectorId), includeArchived, showArchivedContacts);
      }
    };

    if (selectedSectorId) {
      reloadContacts();
    }
  }, [showArchivedContacts, showRecentContacts, showPendingContacts]);

  // Polling de contatos a cada 5s para manter a lista atualizada sem depender apenas do WebSocket
  useEffect(() => {
    if (!selectedSectorId) return;

    const poll = async () => {
      if (isContactsRefreshingRef.current) return;
      isContactsRefreshingRef.current = true;
      try {
        const sectorId = Number(selectedSectorId);
        const includeArchived = false;

        // Carregar contatos para exibição (grupos têm polling próprio a cada 10s)
        await loadContacts(sectorId, includeArchived, showArchivedContacts);

        // Sempre atualizar contagem de arquivados e, quando em Arquivadas, também Pendentes e Recentes
        const [archivedData, nonArchivedData] = await Promise.all([
          getContactsWithFilters(sectorId, false, true),
          showArchivedContacts ? getContactsWithFilters(sectorId, false, false) : null
        ]);
        const archivedList = Array.isArray(archivedData) ? archivedData : (archivedData as any)?.data || [];
        setArchivedContactsCount(archivedList.length);

        // Quando em Arquivadas, contacts só tem arquivados - buscar non-archived para contar Pendentes e Recentes
        if (showArchivedContacts && nonArchivedData) {
          const nonArchivedList = Array.isArray(nonArchivedData) ? nonArchivedData : (nonArchivedData as any)?.data || [];
          const threeDaysAgo = dayjs().subtract(3, 'day');
          const pending = nonArchivedList.filter((c: any) => c.contactStatus === 'Aguardando atendimento').length;
          const recent = nonArchivedList.filter((c: any) => {
            const lastTime = c.lastMessageTime ?? c.last_message_time ?? c.updatedAt ?? c.createdAt;
            return lastTime && dayjs(lastTime).isAfter(threeDaysAgo);
          }).length;
          setPendingContactsCount(pending);
          setRecentContactsCount(recent);
        }
      } catch (error) {
        // Silenciar erros de polling
      } finally {
        isContactsRefreshingRef.current = false;
      }
    };

    const intervalId = window.setInterval(poll, 5000);
    return () => window.clearInterval(intervalId);
  }, [selectedSectorId, showArchivedContacts, showRecentContacts, showPendingContacts]);

  // Polling de grupos a cada 10s para refletir alterações no WhatsApp (foto, nome, configuração)
  useEffect(() => {
    if (!selectedSectorId) return;
    const sectorId = Number(selectedSectorId);
    const tick = () => loadGroups(sectorId);
    tick();
    const intervalId = window.setInterval(tick, 10000);
    return () => window.clearInterval(intervalId);
  }, [selectedSectorId, loadGroups]);

  // Atualizar chave de cache da foto dos grupos a cada 2 min para forçar novo carregamento
  useEffect(() => {
    const id = window.setInterval(() => {
      setGroupPhotoRefreshAt(Math.floor(Date.now() / 120000));
    }, 120000);
    return () => window.clearInterval(id);
  }, []);

  // Polling de mensagens do grupo a cada 5s (apenas polling, sem WebSocket) - garante que as mensagens retornem
  const mergeGroupMessages = useCallback((list: GroupMessageDto[], prev: GroupMessageDto[]) => {
    const listIds = new Set(list.map((m) => m.id));
    const listWhatsAppIds = new Set(list.map((m) => m.whatsAppMessageId).filter(Boolean));
    const toAdd = prev.filter(
      (p) =>
        p.sentByUs &&
        !listIds.has(p.id) &&
        !(p.whatsAppMessageId && listWhatsAppIds.has(p.whatsAppMessageId))
    );
    return [...list, ...toAdd].sort(
      (a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf()
    );
  }, []);

  const applyGroupMessagesFromServer = useCallback((list: GroupMessageDto[]) => {
    setGroupMessages((prev) => {
      const merged = mergeGroupMessages(list, prev);
      maybePlayIncomingGroupMessageSound(prev, merged, groupIncomingSoundSkipRef);
      return merged;
    });
  }, [mergeGroupMessages]);

  useEffect(() => {
    if (!selectedGroup) return;
    groupIncomingSoundSkipRef.current = true;
    const pollMessages = () => {
      getGroupMessages(selectedGroup.id)
        .then((list) => applyGroupMessagesFromServer(list))
        .catch(() => {});
    };
    pollMessages();
    const intervalId = window.setInterval(pollMessages, 5000);
    return () => window.clearInterval(intervalId);
  }, [selectedGroup?.id, applyGroupMessagesFromServer]);

  // Listener para grupos atualizados (evento do polling de eventos) - dispara refresh imediato
  useEffect(() => {
    const handler = (e: CustomEvent<{ sectorId: number }>) => {
      if (e.detail?.sectorId && selectedSectorId && Number(selectedSectorId) === e.detail.sectorId) {
        loadGroups(e.detail.sectorId);
        if (selectedGroup) {
          getGroupMessages(selectedGroup.id)
            .then((list) => applyGroupMessagesFromServer(list))
            .catch(() => {});
        }
      }
    };
    window.addEventListener('groupsUpdated', handler as EventListener);
    return () => window.removeEventListener('groupsUpdated', handler as EventListener);
  }, [selectedSectorId, selectedGroup, loadGroups, applyGroupMessagesFromServer]);

  // WebSocket para atualizações em tempo real
  useEffect(() => {
    const token = SessionService.getToken();
    if (!token || !selectedSectorId) return;

    const handleWebSocketMessage = (msg: any) => {
      const contactOpen = selectedContactRef.current;

      if (msg.type === 'message_status') {
        const p = msg.payload;
        if (p && contactOpen && Number(p.contactID) === Number(contactOpen.id)) {
          const nextStatus = p.messageStatus as Message['messageStatus'];
          setMessages(prev =>
            prev.map(m => {
              if (m.id === p.id) return { ...m, messageStatus: nextStatus };
              if (p.whatsappMessageID && m.whatsappMessageID === p.whatsappMessageID) {
                return { ...m, messageStatus: nextStatus };
              }
              return m;
            })
          );
        }
        return;
      }

      if (msg.type === 'incoming_read_sync') {
        const p = msg.payload;
        const c = selectedContactRef.current;
        if (p && c && Number(p.contactID) === Number(c.id)) {
          if (p.allForContact) {
            setMessages(prev => prev.map(m => (!m.isSent ? { ...m, isRead: true } : m)));
            updateHasNewMessages(prev => ({
              ...prev,
              [Number(p.contactID)]: false,
            }));
          } else {
            const items = Array.isArray(p.items) ? p.items : [];
            const idSet = new Set(items.map((i: { id?: number }) => Number(i.id)).filter((n: number) => !Number.isNaN(n)));
            const waSet = new Set(
              items
                .map((i: { whatsappMessageID?: string }) => i.whatsappMessageID)
                .filter((x: string | undefined): x is string => typeof x === 'string' && x.length > 0)
            );
            setMessages(prev =>
              prev.map(m => {
                if (m.isSent) return m;
                if (idSet.has(m.id)) return { ...m, isRead: true };
                if (m.whatsappMessageID && waSet.has(m.whatsappMessageID)) return { ...m, isRead: true };
                return m;
              })
            );
          }
        }
        return;
      }

      if (msg.type === 'group_message_reaction') {
        const p = msg.payload;
        const g = selectedGroupRef.current;
        if (p && g && Number(p.groupId) === Number(g.id)) {
          const emoji =
            typeof p.reactionEmoji === 'string' && p.reactionEmoji.trim() !== ''
              ? p.reactionEmoji
              : undefined;
          setGroupMessages((prev) =>
            prev.map((gm) => {
              if (gm.id === p.id) return { ...gm, reacaoContato: emoji ?? null };
              if (p.whatsappMessageID && gm.whatsAppMessageId === p.whatsappMessageID) {
                return { ...gm, reacaoContato: emoji ?? null };
              }
              return gm;
            })
          );
        }
        return;
      }

      if (msg.type === 'group_message_reaction_operator') {
        const p = msg.payload;
        const g = selectedGroupRef.current;
        if (p && g && Number(p.groupId) === Number(g.id)) {
          const emoji =
            typeof p.operatorReactionEmoji === 'string' && p.operatorReactionEmoji.trim() !== ''
              ? p.operatorReactionEmoji
              : undefined;
          setGroupMessages((prev) =>
            prev.map((gm) => {
              if (gm.id === p.id) return { ...gm, reacaoOperador: emoji ?? null };
              if (p.whatsappMessageID && gm.whatsAppMessageId === p.whatsappMessageID) {
                return { ...gm, reacaoOperador: emoji ?? null };
              }
              return gm;
            })
          );
        }
        return;
      }

      if (msg.type === 'message_reaction') {
        const p = msg.payload;
        if (p && contactOpen && Number(p.contactID) === Number(contactOpen.id)) {
          const emoji =
            typeof p.reactionEmoji === 'string' && p.reactionEmoji.trim() !== ''
              ? p.reactionEmoji
              : undefined;
          setMessages(prev =>
            prev.map(m => {
              if (m.id === p.id) return { ...m, reactionEmoji: emoji };
              if (p.whatsappMessageID && m.whatsappMessageID === p.whatsappMessageID) {
                return { ...m, reactionEmoji: emoji };
              }
              return m;
            })
          );
        }
        return;
      }

      if (msg.type === 'message_reaction_operator') {
        const p = msg.payload;
        if (p && contactOpen && Number(p.contactID) === Number(contactOpen.id)) {
          const emoji =
            typeof p.operatorReactionEmoji === 'string' && p.operatorReactionEmoji.trim() !== ''
              ? p.operatorReactionEmoji
              : undefined;
          setMessages(prev =>
            prev.map(m => {
              if (m.id === p.id) return { ...m, operatorReactionEmoji: emoji };
              if (p.whatsappMessageID && m.whatsappMessageID === p.whatsappMessageID) {
                return { ...m, operatorReactionEmoji: emoji };
              }
              return m;
            })
          );
        }
        return;
      }

      if (msg.type === 'message') {
        const messageData = msg.payload;

        if (!messageData.isSent) {
          playMessageNotificationSound();
        }

        // Se a mensagem é para o contato selecionado, adicionar às mensagens
        if (contactOpen && Number(messageData.contactID) === Number(contactOpen.id)) {
          const newMessage: Message = {
            id: messageData.id || Date.now(),
            content: messageData.content || '',
            mediaType: messageData.mediaType || 'text',
            mediaUrl: messageData.mediaUrl || messageData.url || null,
            fileName: messageData.fileName || null,
            mimeType: messageData.mimeType || null,
            sectorId: messageData.sectorId || Number(selectedSectorId),
            contactID: messageData.contactID,
            sentAt: normalizeSentAtToLocal(messageData.sentAt || new Date().toISOString()),
            isSent: messageData.isSent || false,
            isRead: messageData.isRead || false,
            whatsappMessageID: messageData.whatsappMessageID || undefined,
            replyToMessageID: messageData.replyToMessageID || undefined,
            repliedMessage: messageData.repliedMessage || undefined,
            messageStatus: (messageData.messageStatus as Message['messageStatus']) || (messageData.isSent ? 'sent' : undefined),
            reactionEmoji:
              messageData.reactionEmoji != null && String(messageData.reactionEmoji).trim() !== ''
                ? messageData.reactionEmoji
                : undefined,
            operatorReactionEmoji:
              messageData.operatorReactionEmoji != null &&
              String(messageData.operatorReactionEmoji).trim() !== ''
                ? messageData.operatorReactionEmoji
                : undefined,
            isForwarded: !!messageData.isForwarded
          };

          setMessages(prev => {
            // Verificar se a mensagem já existe pelo ID real
            if (prev.some(m => m.id === newMessage.id)) {
              return prev;
            }

            // Mesmo envio: id do cliente ≠ id do WS, mas WhatsappMensagemId igual (evita duplicata)
            if (newMessage.isSent && newMessage.whatsappMessageID) {
              const waIdx = prev.findIndex(m => m.whatsappMessageID === newMessage.whatsappMessageID);
              if (waIdx !== -1) {
                const newPrev = [...prev];
                newPrev[waIdx] = { ...newPrev[waIdx], ...newMessage, id: newMessage.id };
                return newPrev.sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
              }
            }

            // Tratamento para evitar duplicidade de mensagens enviadas (Race Condition WS vs API)
            // Se a mensagem recebida foi enviada por nós (isSent) e temos uma mensagem local com status 'sending' com o mesmo conteúdo
            if (newMessage.isSent) {
              const pendingMessageIndex = prev.findIndex(m =>
                m.status === 'sending' &&
                (
                  (m.mediaType === 'text' && outgoingTextMatches(m.content || '', newMessage.content || '')) ||
                  (m.mediaType !== 'text' && m.fileName === newMessage.fileName)
                ) &&
                m.mediaType === newMessage.mediaType
              );

              if (pendingMessageIndex !== -1) {
                const old = prev[pendingMessageIndex];
                console.log('[Ligchat:ticks] WS merged pending → sent', {
                  oldId: old.id,
                  newId: newMessage.id,
                  messageStatus: newMessage.messageStatus,
                  whatsappMessageID: newMessage.whatsappMessageID,
                  optimisticPreview: (old.content || '').slice(0, 120),
                  serverPreview: (newMessage.content || '').slice(0, 120)
                });
                // Substituir a mensagem temporária pela mensagem real do WebSocket
                const newPrev = [...prev];
                newPrev[pendingMessageIndex] = newMessage;
                return newPrev.sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
              }
              const orphanSending = prev.find(m => m.status === 'sending' && m.mediaType === 'text');
              if (orphanSending) {
                console.warn('[Ligchat:ticks] WS isSent mas não fez match com pending', {
                  newId: newMessage.id,
                  messageStatus: newMessage.messageStatus,
                  optimisticPreview: (orphanSending.content || '').slice(0, 120),
                  serverPreview: (newMessage.content || '').slice(0, 120),
                  outgoingTextMatches: outgoingTextMatches(orphanSending.content || '', newMessage.content || '')
                });
              }
            }

            // Adicionar nova mensagem e ordenar
            return [...prev, newMessage].sort((a, b) => {
              const diff = dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf();
              return diff;
            });
          });

        }

        const previewText = getMessagePreviewText({
          content: messageData.content,
          mediaType: messageData.mediaType,
          fileName: messageData.fileName
        });
        if (previewText) {
          setContacts(prev => prev.map(c =>
            Number(c.id) === Number(messageData.contactID)
              ? { ...c, lastMessage: previewText, lastMessageTime: messageData.sentAt }
              : c
          ));
        }

        // Atualizar status de mensagens não lidas
        if (!messageData.isSent) {
          const cid = Number(messageData.contactID);
          updateHasNewMessages(prev => ({
            ...prev,
            [cid]: true
          }));
        }
      }

      if (msg.type === 'contact_responsible_update') {
        const payload = msg.payload;
        console.log('[ChatMobile] Contact responsible update received:', payload);

        // Notificação quando a transferência é para o usuário atual
        if (currentUserId && payload.responsibleId === currentUserId) {
          const contactId = Number(payload.contactId);
          const contact = contactsRef.current.find(c => c.id === contactId);
          const contactName = contact?.name || payload.contactName || 'Contato';
          addNotification('transfer', contactId, contactName);
        }

        // Atualizar contato selecionado se for o mesmo
        if (contactOpen && Number(payload.contactId) === contactOpen.id) {
          setSelectedContact(prev => prev ? {
            ...prev,
            assignedTo: payload.responsibleId,
            responsibleName: payload.responsibleName,
            ...(payload.aiActive !== undefined && { aiActive: payload.aiActive })
          } : null);
        }

        // Atualizar lista de contatos
        setContacts(prev => prev.map(c =>
          Number(c.id) === Number(payload.contactId)
            ? { ...c, assignedTo: payload.responsibleId, responsibleName: payload.responsibleName, ...(payload.aiActive !== undefined && { aiActive: payload.aiActive }) }
            : c
        ));
      }
    };

    const handleContactsList = (newContacts: any[]) => {
      if (!Array.isArray(newContacts)) return;
      setContacts(prev => mergeContactsList(prev, newContacts));
      setSelectedContact(prev => {
        if (!prev) return null;
        const row = newContacts.find((c: any) => Number(c.id) === Number(prev.id));
        if (!row) return prev;
        return mergeContactsList([prev], [row])[0];
      });
    };

    const handleContactUpdate = (updatedContact: any) => {
      setContacts(prev => {
        const contactId = Number(updatedContact.id);
        const oldContact = prev.find(c => Number(c.id) === contactId);

        if (updatedContact.contactStatus === 'Aguardando atendimento') {
          const wasWaiting = oldContact?.contactStatus === 'Aguardando atendimento';
          if (!wasWaiting) {
            const contactName = updatedContact.name || oldContact?.name || 'Contato';
            addNotification('pending', contactId, contactName);
          }
        }

        const index = prev.findIndex(c => Number(c.id) === contactId);
        let newContacts = [...prev];

        if (index >= 0) {
          newContacts[index] = { ...newContacts[index], ...updatedContact };
        } else {
          newContacts.push(updatedContact);
        }

        const pendingCount = newContacts.filter(c => c.contactStatus === 'Aguardando atendimento' && c.archived !== true).length;
        const threeDaysAgo = dayjs().subtract(3, 'day');
        const recentCount = newContacts.filter(c => {
          if (c.archived === true) return false;
          const lastTime = c.lastMessageTime || (c as any).last_message_time || (c as any).updatedAt || c.createdAt;
          return lastTime && dayjs(lastTime).isAfter(threeDaysAgo);
        }).length;
        setPendingContactsCount(pendingCount);
        setRecentContactsCount(recentCount);

        return newContacts;
      });
    };

    // Igual ao ChatWeb: connect atualiza onMessageCallback; handlers para o broadcast interno.
    // Antes: só addMessageHandler quando já conectado → onMessageCallback ficava desatualizado e o efeito
    // reexecutava a cada mudança de referência de selectedContact (ex.: após loadContacts), gerando corrida com o envio.
    ChatWebSocketService.connect(token, handleWebSocketMessage, Number(selectedSectorId))
      .catch((error) => {
        console.error('[ChatMobile] WebSocket connect error:', error);
      });
    ChatWebSocketService.addMessageHandler(handleWebSocketMessage);
    ChatWebSocketService.addContactsListHandler(handleContactsList);
    ChatWebSocketService.addContactHandler(handleContactUpdate);

    return () => {
      ChatWebSocketService.removeMessageHandler(handleWebSocketMessage);
      ChatWebSocketService.removeContactsListHandler(handleContactsList);
      ChatWebSocketService.removeContactHandler(handleContactUpdate);
    };
  }, [selectedSectorId, updateHasNewMessages, showArchivedContacts, currentUserId, addNotification]);

  // Verificar microfone
  useEffect(() => {
    const checkMicrophoneAvailability = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setIsMicrophoneAvailable(true);
      } catch (error) {
        setIsMicrophoneAvailable(false);
      } finally {
        setIsCheckingMicrophone(false);
      }
    };

    checkMicrophoneAvailability();
  }, []);



  // Monitorar scroll do usuário
  useEffect(() => {
    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const currentScrollTop = container.scrollTop;
      const scrollDifference = Math.abs(currentScrollTop - lastScrollTopRef.current);

      // Se o usuário fez scroll manual (diferença significativa)
      if (scrollDifference > 10) {
        isUserScrollingRef.current = true;

        // Reset após um tempo
        setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 1000);
      }

      lastScrollTopRef.current = currentScrollTop;

      // Mostrar/ocultar botão de scroll para baixo
      const threshold = 120;
      const isNearBottom = currentScrollTop >= (container.scrollHeight - container.clientHeight - threshold);
      showScrollToBottomRef.current = !isNearBottom;
      setShowScrollToBottom(!isNearBottom);
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Monitorar scroll do container de mensagens do grupo
  useEffect(() => {
    const handleScrollGroup = () => {
      const container = groupMessagesContainerRef.current;
      if (!container) return;

      const currentScrollTop = container.scrollTop;
      const threshold = 120;
      const isNearBottom = currentScrollTop >= (container.scrollHeight - container.clientHeight - threshold);
      showScrollToBottomGroupRef.current = !isNearBottom;
    };

    const container = groupMessagesContainerRef.current;
    if (container && selectedGroup) {
      container.addEventListener('scroll', handleScrollGroup, { passive: true });
      return () => container.removeEventListener('scroll', handleScrollGroup);
    }
  }, [selectedGroup, groupMessages.length]);

  // Contar filtros ativos
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (selectedFilterUser !== null) count++;
    if (selectedFilterTag !== null) count++;
    return count;
  }, [selectedFilterUser, selectedFilterTag]);

  // Recontar Pendentes e Recentes; novos aguardando só no sino (não toast ao trocar de aba)
  useEffect(() => {
    const currentPending = contacts.filter(c => c.contactStatus === 'Aguardando atendimento' && c.archived !== true);
    const threeDaysAgo = dayjs().subtract(3, 'day');
    const currentRecent = contacts.filter(c => {
      if (c.archived === true) return false;
      const lastTime = c.lastMessageTime || (c as any).last_message_time || (c as any).updatedAt || c.createdAt;
      return lastTime && dayjs(lastTime).isAfter(threeDaysAgo);
    });
    setPendingContactsCount(currentPending.length);
    setRecentContactsCount(currentRecent.length);

    const currentPendingIds = new Set(currentPending.map(c => c.id));
    const prevIds = previousWaitingIdsRef.current;

    if (prevIds.size === 0) {
      previousWaitingIdsRef.current = currentPendingIds;
      return;
    }

    currentPendingIds.forEach(id => {
      if (!prevIds.has(id)) {
        const contact = currentPending.find(c => c.id === id);
        if (contact) addNotification('pending', contact.id, contact.name);
      }
    });

    previousWaitingIdsRef.current = currentPendingIds;
  }, [contacts, addNotification]);

  return (
    <div className="cmob-container">
      <WebSocketStatus />

      {/* Notificações de transferência: exibidas no sino do menu, não como toast */}

      {/* Lista de contatos */}
      {showContactsList && (
        <div className="cmob-contacts-view">
          <div className="cmob-contacts-header">
            <button
              className="cmob-new-contact-btn"
              onClick={() => selectedSectorId && setShowNewContactModal(true)}
              disabled={!selectedSectorId}
            >
              + Novo contato
            </button>
            <button
              className="cmob-filters-btn"
              onClick={() => selectedSectorId && setShowFiltersModal(true)}
              disabled={!selectedSectorId}
            >
              Filtros{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
            </button>
          </div>

          <div className="cmob-search-filters-grid">
            <div className="cmob-search">
              <input
                type="text"
                placeholder={selectedSectorId ? "Buscar contatos..." : "Selecione um setor primeiro"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!selectedSectorId}
              />
            </div>

            {selectedSectorId && (
            <div className="cmob-filter-buttons-grid">
              <button
                className={`cmob-recent-button ${showRecentContacts ? 'active' : ''}`}
                onClick={() => {
                  setShowArchivedContacts(false);
                  setShowRecentContacts(prev => !prev);
                  setShowPendingContacts(false);
                  setShowGroupsView(false);
                }}
              >
                <div className="cmob-archived-content">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>Recentes</span>
                  {recentContactsCount > 0 && (
                    <span className="cmob-recent-count">{formatCount(recentContactsCount)}</span>
                  )}
                </div>
              </button>
              <button
                className={`cmob-pending-button ${showPendingContacts ? 'active' : ''}`}
                onClick={() => {
                  setShowArchivedContacts(false);
                  setShowRecentContacts(false);
                  setShowPendingContacts(prev => !prev);
                  setShowGroupsView(false);
                }}
              >
                <div className="cmob-archived-content">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <span>Pendentes</span>
                  {pendingContactsCount > 0 && (
                    <span className="cmob-pending-count">{formatCount(pendingContactsCount)}</span>
                  )}
                </div>
              </button>
              <button
                className={`cmob-recent-button ${showGroupsView ? 'active' : ''}`}
                onClick={() => {
                  setShowArchivedContacts(false);
                  setShowRecentContacts(false);
                  setShowPendingContacts(false);
                  setShowGroupsView(prev => !prev);
                }}
              >
                <div className="cmob-archived-content">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span>Grupos</span>
                  <span className="cmob-archived-count">{formatCount(groups.length)}</span>
                  {groups.filter(g => g.unreadCount > 0).length > 0 && (
                    <span className="cmob-pending-count">{formatCount(groups.reduce((a, g) => a + g.unreadCount, 0))}</span>
                  )}
                </div>
              </button>
              <button
                className={`cmob-archived-button ${showArchivedContacts ? 'active' : ''}`}
                onClick={() => {
                  setShowArchivedContacts(prev => !prev);
                  setShowRecentContacts(false);
                  setShowPendingContacts(false);
                  setShowGroupsView(false);
                }}
              >
                <div className="cmob-archived-content">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="21 8 21 21 3 21 3 8"></polyline>
                    <rect x="1" y="3" width="22" height="5"></rect>
                    <line x1="10" y1="12" x2="14" y2="12"></line>
                  </svg>
                  <span>Arquivadas</span>
                  <span className="cmob-archived-count">{formatCount(archivedContactsCount)}</span>
                </div>
              </button>
            </div>
            )}
          </div>

          <div className="cmob-contacts">
            {!selectedSectorId ? (
              <div className="cmob-no-sector">
                <div>🔍</div>
                <span>Selecione um setor no menu principal para visualizar os contatos</span>
              </div>
            ) : showGroupsView ? (
              [...groups]
                .filter((g) => {
                  const q = searchTerm.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    (g.name ?? '').toLowerCase().includes(q) ||
                    (g.lastMessage ?? '').toLowerCase().includes(q)
                  );
                })
                .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf())
                .map((group, index) => (
                  <div
                    key={group.id}
                    className="cmob-contact-item-wrapper"
                    style={{ animationDelay: `${Math.min(index * 30, 350)}ms` }}
                  >
                    <div
                      className={`cmob-contact-item ${selectedGroup?.id === group.id ? 'selected' : ''} ${group.unreadCount > 0 ? 'has-new-message' : ''}`}
                      onClick={() => handleGroupSelect(group)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="cmob-contact-avatar">
                        {group.photoUrl ? (
                          <img
                            src={processAvatarUrl(group.photoUrl, { timestamp: groupPhotoRefreshAt }) ?? group.photoUrl}
                            alt={group.name}
                            onClick={(e) => {
                              e.stopPropagation();
                              openAvatarViewer(group.photoUrl!, group.name);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                openAvatarViewer(group.photoUrl!, group.name);
                              }
                            }}
                          />
                        ) : (
                          <div className="cmob-avatar-initials">
                            <FiUsers size={20} />
                          </div>
                        )}
                        {group.unreadCount > 0 && (
                          <span className="cmob-unread-badge">{group.unreadCount > 99 ? '99+' : group.unreadCount}</span>
                        )}
                      </div>
                      <div className="cmob-contact-details">
                        <span className="cmob-contact-name">{group.name}</span>
                        <div className="cmob-contact-meta">
                          <span className="cmob-contact-last" title={group.lastMessage || undefined}>
                            {group.lastMessage ? formatWhatsAppText(group.lastMessage, groupMentionResolver) : 'Sem mensagens'}
                          </span>
                          {group.lastMessageTime && (
                            <span className="cmob-contact-time">{dayjs(group.lastMessageTime).format('HH:mm')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              (() => {
                const baseList = filteredContacts;
                const sortedList = [...baseList].sort((a, b) => {
                  const aoRaw = (a as any).order;
                  const boRaw = (b as any).order;
                  const ao = (aoRaw === undefined || aoRaw === null) ? Number.MAX_SAFE_INTEGER : aoRaw;
                  const bo = (boRaw === undefined || boRaw === null) ? Number.MAX_SAFE_INTEGER : boRaw;
                  if (ao !== bo) return ao - bo;
                  const at = (a.lastMessageTime || a.createdAt) ?? '';
                  const bt = (b.lastMessageTime || b.createdAt) ?? '';
                  const ad = dayjs(at);
                  const bd = dayjs(bt);
                  return (bd.isValid() ? bd.valueOf() : 0) - (ad.isValid() ? ad.valueOf() : 0);
                });
                return sortedList.map((contact, index) => (
                  <div
                    key={contact.id}
                    className="cmob-contact-item-wrapper"
                    style={{ animationDelay: `${Math.min(index * 30, 350)}ms` }}
                  >
                    <ContactItem
                    contact={{
                      id: contact.id,
                      name: contact.name,
                      number: contact.number,
                      email: contact.email,
                      avatarUrl: contact.avatarUrl,
                      lastMessage: contact.lastMessage,
                      lastMessageTime: contact.lastMessageTime,
                      tagId: contact.tagId,
                      tagColor: tags.find(t => t.id === contact.tagId)?.color,
                      responsibleName: contact.responsibleName
                    }}
                    isSelected={selectedContact?.id === contact.id}
                    onSelect={() => handleContactSelect(contact)}
                    onAvatarClick={openAvatarViewer}
                    hasNewMessage={(contact as any).isViewed === false || (contact as any).is_viewed === false}
                    showArchivedIndicator={false}
                  />
                  </div>
                ));
              })()
            )}
          </div>
        </div>
      )}

      {/* Chat view - contato */}
      {!showContactsList && selectedContact && !selectedGroup && (
        <div className="cmob-chat-view">
          {/* Header do chat */}
          <div className="cmob-header">
            <button className="cmob-back-button" onClick={handleBackToContacts}>
              <FiArrowLeft />
            </button>

            <div className="cmob-header-contact">
              <div className="cmob-header-avatar">
                {selectedContact.avatarUrl ? (
                  <img
                    src={selectedContact.avatarUrl}
                    alt={selectedContact.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      openAvatarViewer(selectedContact.avatarUrl!, selectedContact.name);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        openAvatarViewer(selectedContact.avatarUrl!, selectedContact.name);
                      }
                    }}
                  />
                ) : (
                  <div className="cmob-avatar-initials">
                    <FiUser size={24} />
                  </div>
                )}
              </div>
              <div className="cmob-header-info">
                <div className="cmob-header-name-row">
                  <span className="cmob-header-name">
                    {selectedContact.name}
                  </span>
                  <button onClick={handleStartNameEdit} className="cmob-edit-name-btn">
                    <FiEdit2 size={16} />
                  </button>
                </div>
                {selectedContact.number && (
                  <div className="cmob-header-phone">
                    {formatPhoneNumber(selectedContact.number)}
                  </div>
                )}
                {selectedContact.contactStatus === 'Aguardando atendimento' && (
                  <div className="cmob-header-waiting-badge">
                    <span className="cmob-waiting-dot"></span>
                    Aguardando atendimento humano
                  </div>
                )}
              </div>
            </div>

            <div className="cmob-ai-toggle" onClick={handleToggleAIActive} title={selectedContact.aiActive ? 'Atendimento por IA — toque para mudar para Humano' : 'Atendimento Humano — toque para mudar para IA'}>
              <span className={`cmob-ai-toggle-label ${selectedContact.aiActive ? 'active' : ''}`}>IA</span>
              <div className={`cmob-ai-toggle-switch ${selectedContact.aiActive ? 'ai' : 'human'}`}>
                <div className="cmob-ai-toggle-thumb" />
              </div>
              <span className={`cmob-ai-toggle-label ${!selectedContact.aiActive ? 'active' : ''}`}>Humano</span>
            </div>

            <button
              className="cmob-header-menu-btn"
              onClick={() => setShowHeaderMenu(!showHeaderMenu)}
            >
              <FiMoreVertical />
            </button>

            {showHeaderMenu && (
              <div className="cmob-header-menu">
                <div className="cmob-header-menu-item" onClick={() => {
                  setShowHeaderMenu(false);
                  setForwardContactSearch(''); setSelectedContactToForward(null); setShowForwardContactModal(true);
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 2L11 13"></path>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
                  </svg>
                  Encaminhar contato
                </div>
                <div className="cmob-header-menu-item" onClick={async () => {
                  if (selectedContact && selectedSectorId) {
                    const success = await markContactAsUnread(Number(selectedSectorId), selectedContact.id);
                    if (success) {
                      setContacts(prev => prev.map(c => 
                        c.id === selectedContact.id ? { ...c, isViewed: false } : c
                      ));
                      setSelectedContact(prev => prev ? { ...prev, isViewed: false } : null);
                    }
                    setShowHeaderMenu(false);
                  }
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                  Marcar como não lida
                </div>
                {canTransferContact() && (
                  <div className="cmob-header-menu-select-group">
                    <label className="cmob-menu-select-label">
                      <FiUserPlus size={14} />
                      Responsável
                    </label>
                    <select
                      className="cmob-menu-select"
                      value={selectedContact?.assignedTo || -1}
                      onChange={(e) => {
                        const userId = Number(e.target.value);
                        handleTransferContact(userId === -1 ? null : userId);
                        setShowHeaderMenu(false);
                      }}
                      disabled={isTransferLoading}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value={-1}>Sem responsável</option>
                      <option value={currentUserId}>Eu</option>
                      {users.filter(user => user.id !== currentUserId).map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="cmob-header-menu-select-group">
                  <label className="cmob-menu-select-label">
                    <FiTag size={14} />
                    Tag
                  </label>
                  <select
                    className="cmob-menu-select"
                    value={selectedContact?.tagId || -1}
                    onChange={(e) => {
                      const tagId = Number(e.target.value);
                      handleUpdateTag(tagId === -1 ? null : tagId);
                      setShowHeaderMenu(false);
                    }}
                    disabled={isTagLoading}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value={-1}>Sem tag</option>
                    {tags.map(tag => (
                      <option key={tag.id} value={tag.id}>{tag.name}</option>
                    ))}
                  </select>
                </div>
                {selectedContact?.contactStatus === 'Aguardando atendimento' && (
                  <div className="cmob-header-menu-item cmob-conclude-item" onClick={handleConcludeAttendance}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    Concluir atendimento
                  </div>
                )}
                {isCurrentUserAdmin && (
                  <div
                    className={`cmob-header-menu-item ${isAnonymous ? 'anonymous-active' : ''}`}
                    onClick={() => {
                      const newValue = !isAnonymous;
                      setIsAnonymous(newValue);
                      SessionService.setIsAnonymous(newValue);
                      setShowHeaderMenu(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                      {isAnonymous && <line x1="1" y1="1" x2="23" y2="23"></line>}
                    </svg>
                    {isAnonymous ? 'Desativar modo anônimo' : 'Ativar modo anônimo'}
                  </div>
                )}
                <div className="cmob-header-menu-item" onClick={() => setShowArchiveModal(true)}>
                  <FiArchive />
                  {selectedContact?.archived ? 'Desarquivar' : 'Arquivar'}
                </div>
                {canDeleteContact() && (
                  <div className="cmob-header-menu-item" onClick={() => setShowDeleteModal(true)}>
                    <FiUsers />
                    Excluir contato
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Área de mensagens */}
          <div className="cmob-messages" ref={messagesContainerRef}>
            {isMessagesLoading && (
              <div className="cmob-messages-loading">
                <div className="cmob-loading-dots">
                  <span></span><span></span><span></span>
                </div>
                <span>Carregando mensagens</span>
              </div>
            )}

            {hasMore && !isMessagesLoading && (
              <button className="cmob-load-more" onClick={() => loadMessages(selectedContact.id, page + 1, true)}>
                {isLoadingMore ? (
                  <div className="cmob-loading-spinner" />
                ) : (
                  'Ver mais'
                )}
              </button>
            )}

            {(() => {
              const elements: JSX.Element[] = [];
              let lastDateKey: string | null = null;
              messages.forEach((msg) => {
                const dateKey = toAppTimezone(msg.sentAt).format('YYYY-MM-DD');
                if (dateKey !== lastDateKey) {
                  elements.push(
                    <div key={`date-${dateKey}`} className="cmob-date-divider">
                      <span>{formatDateHeader(msg.sentAt)}</span>
                    </div>
                  );
                  lastDateKey = dateKey;
                }
                elements.push(renderMessage(msg));
              });
              return elements;
            })()}

            <div ref={messagesEndRef} />

            {showScrollToBottom && (
              <button className="cmob-scroll-to-bottom" onClick={() => scrollToBottom()}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
                </svg>
              </button>
            )}
          </div>

          {/* Área de input */}
          <div className="cmob-input-area">
            {/* Preview da mensagem respondida (estilo WhatsApp) */}
            {replyingTo && (
              <div className="cmob-reply-preview-whatsapp">
                <div className="cmob-reply-preview-header">
                  <span className="cmob-reply-preview-name">
                    {replyingTo.isSent ? 'Você' : (selectedContact?.name || 'Contato')}
                  </span>
                  <button
                    className="cmob-reply-preview-cancel-whatsapp"
                    onClick={handleCancelReply}
                    title="Cancelar resposta"
                  >
                    ×
                  </button>
                </div>
                <div className="cmob-reply-preview-message">
                  {replyingTo.mediaType === 'text' ? (
                    formatWhatsAppText(replyingTo.content || '')
                  ) : (
                    <span className="cmob-reply-preview-media">
                      {replyingTo.mediaType === 'image' && 'Imagem'}
                      {replyingTo.mediaType === 'sticker' && 'Figurinha'}
                      {replyingTo.mediaType === 'gif' && 'GIF'}
                      {replyingTo.mediaType === 'audio' && '🎵 Áudio'}
                      {replyingTo.mediaType === 'document' && 'Documento'}
                      {replyingTo.mediaType === 'video' && '🎥 Vídeo'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {isRecording ? (
              <AudioRecorder
                onSend={handleSendAudio}
                onCancel={() => setIsRecording(false)}
              />
            ) : (
              <div className="cmob-input-container cmob-input-container-with-emoji" ref={emojiAnchorRef}>
                <div className="cmob-input-actions">
                  <button
                    className="cmob-input-button"
                    title="Anexar imagem"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={!selectedSectorId}
                  >
                    <ImageIcon />
                  </button>
                  <button
                    className="cmob-input-button"
                    title="Anexar documento"
                    onClick={() => documentInputRef.current?.click()}
                    disabled={!selectedSectorId}
                  >
                    <DocumentIcon />
                  </button>
                  <button
                    ref={emojiButtonRef}
                    type="button"
                    className={`cmob-input-button ${showEmojiPicker ? 'active' : ''}`}
                    title="Emojis"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                    disabled={!selectedSectorId}
                  >
                    <FiSmile size={20} />
                  </button>
                  <button
                    className="cmob-input-button"
                    title="Gravar áudio"
                    onClick={() => isMicrophoneAvailable && setIsRecording(true)}
                    disabled={!isMicrophoneAvailable || !selectedSectorId}
                  >
                    <MicIcon />
                  </button>
                  {isCurrentUserAdmin && (
                    <button
                      className={`cmob-input-button ${isAnonymous ? 'active' : ''}`}
                      title={isAnonymous ? "Desativar modo anônimo" : "Ativar modo anônimo"}
                      onClick={() => {
                        const newValue = !isAnonymous;
                        setIsAnonymous(newValue);
                        SessionService.setIsAnonymous(newValue);
                      }}
                      disabled={!selectedSectorId}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                        {isAnonymous && <line x1="1" y1="1" x2="23" y2="23"></line>}
                      </svg>
                    </button>
                  )}
                </div>

                {showEmojiPicker && (
                  <div className="cmob-emoji-picker-wrap">
                    <EmojiPicker
                      isOpen={showEmojiPicker}
                      onEmojiSelect={handleEmojiSelect}
                      anchorRef={emojiButtonRef}
                    />
                  </div>
                )}
                <textarea
                  ref={messageInputRef}
                  className="cmob-input"
                  placeholder={selectedSectorId ? "Digite uma mensagem..." : "Selecione um setor para enviar mensagens"}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={!selectedSectorId}
                  rows={1}
                />

                <button
                  className="cmob-send-button"
                  onClick={() => messageInput.trim() && handleSendMessage()}
                  disabled={!messageInput.trim() || !selectedSectorId}
                >
                  <SendIcon />
                </button>

                {/* Input de arquivo oculto */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <input
                  ref={documentInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat view - grupo (somente leitura) */}
      {!showContactsList && selectedGroup && (
        <div className="cmob-chat-view">
          <div className="cmob-header">
            <button className="cmob-back-button" onClick={handleBackToContacts}>
              <FiArrowLeft />
            </button>
            <div className="cmob-header-contact">
              <div className="cmob-header-avatar">
                {selectedGroup.photoUrl ? (
                  <img
                    src={processAvatarUrl(selectedGroup.photoUrl, { timestamp: groupPhotoRefreshAt }) ?? selectedGroup.photoUrl}
                    alt={selectedGroup.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      openAvatarViewer(selectedGroup.photoUrl!, selectedGroup.name);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        openAvatarViewer(selectedGroup.photoUrl!, selectedGroup.name);
                      }
                    }}
                  />
                ) : (
                  <div className="cmob-avatar-initials">
                    <FiUsers size={24} />
                  </div>
                )}
              </div>
              <div className="cmob-header-info">
                <span className="cmob-header-name">{selectedGroup.name}</span>
                <span className="cmob-header-meta">{selectedGroup.participantCount} participantes</span>
              </div>
            </div>
            <button type="button" className="cmob-header-action" onClick={handleExcluirGrupo} title="Excluir grupo do Ligchat">
              <FiMoreVertical />
            </button>
          </div>
          <div className="cmob-messages cmob-messages-container" ref={groupMessagesContainerRef}>
            {groupMessages.length === 0 ? (
              <div className="cmob-no-messages">Nenhuma mensagem no grupo</div>
            ) : (
              groupMessages.map((gm) => {
                const isUnread = !gm.sentByUs && selectedGroup?.lastViewedAt && dayjs(gm.sentAt).isAfter(dayjs(selectedGroup.lastViewedAt));
                const repliedGroupMsg = gm.replyToMessageId ? groupMessages.find((m) => m.whatsAppMessageId === gm.replyToMessageId) : null;
                return (
                  <div key={gm.id} id={`group-message-${gm.id}`} className={`cmob-message-wrapper ${gm.sentByUs ? 'sent' : 'received'} ${isUnread ? 'cmob-group-msg-unread' : ''} ${replyingToGroupMessage?.id === gm.id ? 'replying-to' : ''} ${gm.replyToMessageId ? 'reply' : ''}`}>
                    <div className="cmob-group-msg-sender">{gm.sentByUs ? 'Você' : (gm.senderName ?? '')}</div>
                    <div
                      className="cmob-message-bubble"
                      onClick={() => handleReplyToGroupMessage(gm)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleReplyToGroupMessage(gm)}
                      aria-label="Responder a esta mensagem"
                    >
                      {gm.isForwarded && (
                        <ForwardedMessageHeader variant="mobile" isSent={!!gm.sentByUs} />
                      )}
                      {gm.replyToMessageId && (
                        <div className="cmob-reply-to-message">
                          <div className="cmob-reply-indicator">
                            <span className="cmob-reply-text">Respondendo a:</span>
                          </div>
                          {repliedGroupMsg ? (
                            <div
                              className="cmob-original-message"
                              onClick={(e) => {
                                e.stopPropagation();
                                const el = document.getElementById(`group-message-${repliedGroupMsg.id}`);
                                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }}
                            >
                              <div className="cmob-original-content">
                                {repliedGroupMsg.type === 'text' || !repliedGroupMsg.type
                                  ? (repliedGroupMsg.content || 'Mensagem')
                                  : repliedGroupMsg.type === 'image'
                                    ? '🖼️ Imagem'
                                    : repliedGroupMsg.type === 'audio'
                                      ? '🎵 Áudio'
                                      : repliedGroupMsg.type === 'video'
                                        ? '🎥 Vídeo'
                                        : repliedGroupMsg.type === 'document'
                                          ? `📄 ${repliedGroupMsg.fileName || 'Documento'}`
                                          : repliedGroupMsg.type === 'sticker' || repliedGroupMsg.type === 'gif'
                                            ? 'Figurinha'
                                            : repliedGroupMsg.type === 'contact'
                                              ? '👤 Contato'
                                              : repliedGroupMsg.type === 'location'
                                                ? '📍 Localização'
                                                : 'Mensagem'}
                              </div>
                              <div className="cmob-original-time">
                                {formatMessageTime(repliedGroupMsg.sentAt)} {repliedGroupMsg.sentByUs ? '(Você)' : `(${repliedGroupMsg.senderName ?? ''})`}
                              </div>
                            </div>
                          ) : (
                            <div className="cmob-original-message">
                              <div className="cmob-original-content">Mensagem anterior</div>
                            </div>
                          )}
                        </div>
                      )}
                      {gm.type === 'image' && gm.url && (
                        <div
                          className="cmob-group-msg-image-wrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAttachmentModal({
                              url: gm.url!,
                              fileName: gm.fileName || 'Imagem',
                              type: 'image',
                              groupMessage: gm
                            });
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setAttachmentModal({
                            url: gm.url!,
                            fileName: gm.fileName || 'Imagem',
                            type: 'image',
                            groupMessage: gm
                          })}
                        >
                          <img src={gm.url} alt="" className="cmob-msg-image" onLoad={() => scrollToBottomGroupIfNearBottom()} />
                        </div>
                      )}
                      {gm.type === 'sticker' && gm.url && (
                        <div className="cmob-message-sticker">
                          <img src={gm.url} alt="Figurinha" className="cmob-msg-sticker" onLoad={() => scrollToBottomGroupIfNearBottom()} />
                        </div>
                      )}
                      {gm.type === 'gif' && gm.url && (
                        <div className="cmob-message-gif">
                          <img src={gm.url} alt="GIF" className="cmob-msg-gif" onLoad={() => scrollToBottomGroupIfNearBottom()} />
                        </div>
                      )}
                      {gm.type === 'audio' && (
                        gm.url ? (
                          <div className="cmob-message-audio" onClick={(e) => e.stopPropagation()}>
                            <AudioMessage src={gm.url} isSent={gm.sentByUs} />
                          </div>
                        ) : (
                          <div className="cmob-message-audio cmob-message-audio--placeholder">
                            <span>🎵 Áudio</span>
                          </div>
                        )
                      )}
                      {gm.type === 'video' && gm.url && (
                        <video controls src={gm.url} className="cmob-msg-video" onLoadedData={() => scrollToBottomGroupIfNearBottom()} />
                      )}
                      {gm.type === 'document' && (
                        gm.url ? (
                          <div
                            className="cmob-message-document"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentModal({
                                url: gm.url!,
                                fileName: gm.fileName || gm.content || 'documento',
                                type: 'document',
                                groupMessage: gm
                              });
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setAttachmentModal({
                              url: gm.url!,
                              fileName: gm.fileName || gm.content || 'documento',
                              type: 'document',
                              groupMessage: gm
                            })}
                          >
                            <DocumentIcon />
                            <span>{gm.fileName || gm.content || 'Documento'}</span>
                            {gm.content && gm.fileName && gm.content !== gm.fileName && (
                              <div className="cmob-message-document-caption">{formatWhatsAppText(gm.content, groupMentionResolver)}</div>
                            )}
                          </div>
                        ) : (
                          <div className="cmob-message-document">
                            <DocumentIcon />
                            <span>{gm.fileName || gm.content || 'Documento'}</span>
                            {gm.content && gm.fileName && gm.content !== gm.fileName && (
                              <div className="cmob-message-document-caption">{formatWhatsAppText(gm.content, groupMentionResolver)}</div>
                            )}
                          </div>
                        )
                      )}
                      {gm.type === 'contact' && gm.content && (() => {
                        const parsed = parseContactShareContent(gm.content);
                        const synthetic = {
                          id: gm.id,
                          content: gm.content,
                          mediaType: 'contact',
                          sentAt: gm.sentAt,
                          isSent: gm.sentByUs,
                        } as Message;
                        return (
                          <ContactShareCard
                            name={parsed.name}
                            phoneLabel={parsed.phoneRaw}
                            timeLabel={formatMessageTime(gm.sentAt)}
                            isSent={!!gm.sentByUs}
                            chatBusy={contactShareBusyMessageId === gm.id}
                            onChat={(e) => {
                              e.stopPropagation();
                              void handleContactShareChat(synthetic);
                            }}
                          />
                        );
                      })()}
                      {gm.type === 'location' && gm.content && (
                        <span className="cmob-msg-text">{gm.content}</span>
                      )}
                      {gm.content && (gm.type === 'text' || !gm.type) && gm.type !== 'contact' && gm.type !== 'location' && (
                        <span className="cmob-msg-text">{formatWhatsAppText(gm.content, groupMentionResolver)}</span>
                      )}
                      {gm.reacaoContato ? (
                        <span className="cmob-message-reaction-badge" aria-label="Reação">
                          {gm.reacaoContato}
                        </span>
                      ) : null}
                      {gm.reacaoOperador ? (
                        <span
                          className="cmob-message-reaction-badge cmob-message-reaction-badge-operator"
                          aria-label="Sua reação"
                        >
                          {gm.reacaoOperador}
                        </span>
                      ) : null}
                      <div className="cmob-msg-footer">
                        {isUnread && <span className="cmob-group-msg-unread-badge">Não lida</span>}
                        {gm.type !== 'contact' && (
                          <span className="cmob-msg-time">{formatMessageTime(gm.sentAt)}</span>
                        )}
                        {gm.sentByUs && (
                          <span className="cmob-message-status sent" style={{ marginLeft: 6 }}>
                            {!gm.whatsAppMessageId || gm.id < 0 ? '✓' : '✓✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} aria-hidden="true" />
          </div>
          {/* Área de input igual ao chat de contato */}
          <div className="cmob-input-area">
            {/* Preview da resposta no grupo */}
            {replyingToGroupMessage && (
              <div className="cmob-reply-preview-whatsapp">
                <div className="cmob-reply-preview-header">
                  <span className="cmob-reply-preview-name">
                    {replyingToGroupMessage.sentByUs ? 'Você' : (replyingToGroupMessage.senderName ?? '')}
                  </span>
                  <button
                    type="button"
                    className="cmob-reply-preview-cancel-whatsapp"
                    onClick={handleCancelReplyGroup}
                    title="Cancelar resposta"
                  >
                    ×
                  </button>
                </div>
                <div className="cmob-reply-preview-message">
                  {replyingToGroupMessage.type === 'text' || !replyingToGroupMessage.type ? (
                    (replyingToGroupMessage.content || '').slice(0, 60) + ((replyingToGroupMessage.content?.length ?? 0) > 60 ? '...' : '')
                  ) : (
                    <span className="cmob-reply-preview-media">
                      {replyingToGroupMessage.type === 'image' && 'Imagem'}
                      {replyingToGroupMessage.type === 'sticker' && 'Figurinha'}
                      {replyingToGroupMessage.type === 'gif' && 'GIF'}
                      {replyingToGroupMessage.type === 'audio' && '🎵 Áudio'}
                      {replyingToGroupMessage.type === 'document' && 'Documento'}
                      {replyingToGroupMessage.type === 'video' && '🎥 Vídeo'}
                      {replyingToGroupMessage.type === 'contact' && 'Contato'}
                      {replyingToGroupMessage.type === 'location' && 'Localização'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {isRecording ? (
              <AudioRecorder
                onSend={(blob) => handleGroupSendAudio(blob)}
                onCancel={() => setIsRecording(false)}
              />
            ) : (
              <div className="cmob-input-container cmob-input-container-with-emoji" ref={emojiAnchorRef}>
                <div className="cmob-input-actions">
                  <button className="cmob-input-button" title="Anexar imagem" onClick={() => groupImageInputRef.current?.click()} disabled={!selectedSectorId}>
                    <ImageIcon />
                  </button>
                  <button className="cmob-input-button" title="Anexar documento" onClick={() => groupDocumentInputRef.current?.click()} disabled={!selectedSectorId}>
                    <DocumentIcon />
                  </button>
                  <button
                    ref={emojiButtonRef}
                    type="button"
                    className={`cmob-input-button ${showEmojiPicker ? 'active' : ''}`}
                    title="Emojis"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                    disabled={!selectedSectorId}
                  >
                    <FiSmile size={20} />
                  </button>
                  <button className="cmob-input-button" title="Gravar áudio" onClick={() => setIsRecording(true)} disabled={!selectedSectorId}>
                    <MicIcon />
                  </button>
                </div>
                {showEmojiPicker && (
                  <div className="cmob-emoji-picker-wrap">
                    <EmojiPicker
                      isOpen={showEmojiPicker}
                      onEmojiSelect={handleEmojiSelect}
                      anchorRef={emojiButtonRef}
                    />
                  </div>
                )}
                <textarea
                  ref={messageInputRef}
                  className="cmob-input"
                  placeholder={selectedSectorId ? 'Digite uma mensagem...' : 'Selecione um setor'}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={!selectedSectorId}
                  rows={1}
                />
                <button
                  className="cmob-send-button"
                  onClick={() => messageInput.trim() && handleSendGroupMessage()}
                  disabled={!messageInput.trim() || !selectedSectorId}
                >
                  <SendIcon />
                </button>
                <input
                  ref={groupImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleGroupFileSelect}
                  style={{ display: 'none' }}
                />
                <input
                  ref={groupDocumentInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                  onChange={handleGroupFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de transferência */}
      {showTransferModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Transferir Contato</h3>
              <button className="cmob-modal-close" onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Selecione o usuário para quem deseja transferir o contato <strong>{selectedContact?.name}</strong>:</p>
              <div className="cmob-user-list">
                {/* Opção Remover responsável */}
                {(selectedContact?.assignedTo !== null) && (
                  <div
                    className={`cmob-user-item cmob-user-item-remove ${selectedUserId === -1 ? 'selected' : ''}`}
                    onClick={() => setSelectedUserId(-1)}
                  >
                    <div className="cmob-user-avatar cmob-avatar-remove">
                      <FiUserPlus className="cmob-avatar-remove-icon" style={{ transform: 'rotate(45deg)' }} />
                    </div>
                    <div className="cmob-user-info">
                      <span className="cmob-user-name cmob-user-name-remove">Remover responsável</span>
                      <span className="cmob-user-status" style={{ marginLeft: '4px' }}> (Deixar sem ninguém atribuído)</span>
                    </div>
                  </div>
                )}

                {/* Opção Transferir para mim */}
                <div
                  className={`cmob-user-item ${selectedContact?.assignedTo === currentUserId ? 'disabled' : selectedUserId === currentUserId ? 'selected' : ''}`}
                  onClick={() => selectedContact?.assignedTo !== currentUserId && setSelectedUserId(currentUserId)}
                >
                  <div className="cmob-user-avatar cmob-avatar-self">
                    {currentUserName ? getInitials(currentUserName) : 'EU'}
                  </div>
                  <div className="cmob-user-info">
                    <span className="cmob-user-name">Transferir para mim</span>
                    {selectedContact?.assignedTo === currentUserId && (
                      <span className="cmob-user-status" style={{ marginLeft: '4px' }}> (Já é o responsável)</span>
                    )}
                  </div>
                </div>

                {/* Outros usuários */}
                {users.filter(user => user.id !== currentUserId).map(user => (
                  <div
                    key={user.id}
                    className={`cmob-user-item ${selectedUserId === user.id ? 'selected' : ''}`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <div className="cmob-user-avatar">
                      {getInitials(user.name)}
                    </div>
                    <span>{user.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowTransferModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleTransferContact}
                disabled={!selectedUserId || isTransferLoading}
              >
                {isTransferLoading ? 'Transferindo...' : 'Transferir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de tag */}
      {showTagModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowTagModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Alterar Tag</h3>
              <button className="cmob-modal-close" onClick={() => setShowTagModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Selecione uma tag para o contato <strong>{selectedContact?.name}</strong>:</p>
              <div className="cmob-tag-list">
                <div
                  className={`cmob-tag-item ${selectedTagId === null ? 'selected' : ''}`}
                  onClick={() => setSelectedTagId(null)}
                >
                  <div className="cmob-tag-color" style={{ backgroundColor: '#666' }}></div>
                  <span>Sem tag</span>
                </div>
                {tags.length === 0 ? (
                  <div className="cmob-tag-item" style={{ opacity: 0.6, cursor: 'default', pointerEvents: 'none' }}>
                    <span>Nenhuma tag cadastrada. Crie tags primeiro.</span>
                  </div>
                ) : (
                tags.map(tag => (
                  <div
                    key={tag.id}
                    className={`cmob-tag-item ${selectedTagId === tag.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTagId(tag.id)}
                  >
                    <div
                      className="cmob-tag-color"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <span>{tag.name}</span>
                  </div>
                ))
                )}
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowTagModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleUpdateTag}
                disabled={isTagLoading}
              >
                {isTagLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão */}
      {showDeleteModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Excluir Contato</h3>
              <button className="cmob-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Tem certeza que deseja excluir o contato <strong>{selectedContact?.name}</strong>?</p>
              <p className="cmob-warning">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-danger"
                onClick={handleDeleteContact}
                disabled={isDeletingContact}
              >
                {isDeletingContact ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de arquivar */}
      {showArchiveModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowArchiveModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>{selectedContact?.archived ? 'Desarquivar' : 'Arquivar'} Contato</h3>
              <button className="cmob-modal-close" onClick={() => setShowArchiveModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>
                Tem certeza que deseja {selectedContact?.archived ? 'desarquivar' : 'arquivar'} o contato <strong>{selectedContact?.name}</strong>?
              </p>
              {!selectedContact?.archived && (
                <p className="cmob-info">O contato será movido para a seção de arquivados.</p>
              )}
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowArchiveModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleArchiveContact}
                disabled={isArchivingContact}
              >
                {isArchivingContact ? (selectedContact?.archived ? 'Desarquivando...' : 'Arquivando...') : (selectedContact?.archived ? 'Desarquivar' : 'Arquivar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de novo contato */}
      {showNewContactModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowNewContactModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Novo Contato</h3>
              <button className="cmob-modal-close" onClick={() => setShowNewContactModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <div className="cmob-form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="Digite o nome do contato"
                />
              </div>
              <div className="cmob-form-group">
                <label>Número *</label>
                <input
                  type="text"
                  value={newContactNumber}
                  onChange={(e) => setNewContactNumber(e.target.value)}
                  placeholder="Digite o número do contato"
                />
              </div>
              {newContactError && (
                <div className="cmob-error">{newContactError}</div>
              )}
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowNewContactModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleCreateContact}
                disabled={isCreatingContact}
              >
                {isCreatingContact ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de filtros */}
      {showFiltersModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowFiltersModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Filtros</h3>
              <button className="cmob-modal-close" onClick={() => setShowFiltersModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Filtre os contatos por:</p>

              {/* Filtro por responsável */}
              <div className="cmob-form-group">
                <label>Responsável</label>
                <select
                  value={selectedFilterUser === null ? '' : selectedFilterUser}
                  onChange={(e) => setSelectedFilterUser(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Todos</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              {/* Filtro por tag */}
              <div className="cmob-form-group">
                <label>Tag</label>
                <select
                  value={selectedFilterTag === null ? '' : selectedFilterTag}
                  onChange={(e) => setSelectedFilterTag(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Todas</option>
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => {
                  setSelectedFilterUser(null);
                  setSelectedFilterTag(null);
                }}
              >
                Limpar Filtros
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={() => setShowFiltersModal(false)}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de editar nome */}
      {showNameEditModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowNameEditModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Editar Nome do Contato</h3>
              <button className="cmob-modal-close" onClick={() => setShowNameEditModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <div className="cmob-form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="Digite o nome do contato"
                  maxLength={50}
                  autoFocus
                />
                <div className="cmob-character-count">
                  {`${editingName.length}/50`}
                </div>
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button className="cmob-btn cmob-btn-secondary" onClick={handleCancelNameEdit}>
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleSaveName}
                disabled={isSavingName || !editingName.trim()}
              >
                {isSavingName ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForwardContactModal && (
        <div className="cmob-modal-overlay" onClick={() => { setShowForwardContactModal(false); setForwardContactSearch(''); setSelectedContactToForward(null); setForwardListOpen(false); }}>
          <div className="cmob-modal cmob-modal-forward" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Encaminhar contato</h3>
              <button className="cmob-modal-close" onClick={() => { setShowForwardContactModal(false); setForwardContactSearch(''); setSelectedContactToForward(null); setForwardListOpen(false); }}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p className="cmob-forward-label">Enviar contato para <strong>{selectedContact?.name}</strong> (escolha qual contato enviar):</p>
              <div className={`cmob-forward-select-wrap ${forwardListOpen ? 'is-open' : ''}`}>
                <div className="cmob-forward-search-row">
                  <input
                    type="text"
                    className="cmob-forward-search"
                    placeholder="Buscar por nome ou número..."
                    value={forwardContactSearch}
                    onChange={(e) => setForwardContactSearch(e.target.value)}
                    onFocus={() => setForwardListOpen(true)}
                  />
                  <button type="button" className="cmob-forward-toggle" onClick={() => setForwardListOpen(!forwardListOpen)} aria-expanded={forwardListOpen} aria-label={forwardListOpen ? 'Fechar lista' : 'Abrir lista de contatos'}>
                    {forwardListOpen ? '▲' : '▼'}
                  </button>
                </div>
                {forwardListOpen && (
                  <div className="cmob-contact-forward-list">
                    {(() => {
                      const list = contacts
                        .filter(c => c.id !== selectedContact?.id && c.name)
                        .filter((c) => {
                          const q = forwardContactSearch.trim();
                          if (!q) return true;
                          const qLower = q.toLowerCase();
                          const qDigits = q.replace(/\D/g, '');
                          const byName = c.name.toLowerCase().includes(qLower);
                          const byPhone =
                            qDigits.length > 0 &&
                            !!c.number &&
                            c.number.replace(/\D/g, '').includes(qDigits);
                          return byName || byPhone;
                        })
                        .sort((a, b) =>
                          a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' })
                        );
                      if (list.length === 0) {
                        return (
                          <div className="cmob-forward-empty">
                            {forwardContactSearch.trim() ? 'Nenhum contato encontrado.' : 'Nenhum outro contato no setor.'}
                          </div>
                        );
                      }
                      return list.map(contact => (
                        <div
                          key={contact.id}
                          className={`cmob-contact-forward-option ${selectedContactToForward?.id === contact.id ? 'selected' : ''}`}
                          onClick={() => { setSelectedContactToForward(contact); setForwardListOpen(false); }}
                        >
                          <span className="cmob-forward-option-name">{contact.name}</span>
                          <span className="cmob-forward-option-phone">{formatPhoneNumber(contact.number)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
              {selectedContactToForward && (
                <p className="cmob-forward-selected">
                  Selecionado: <strong>{selectedContactToForward.name}</strong>
                </p>
              )}
            </div>
            <div className="cmob-modal-actions">
              <button className="cmob-btn cmob-btn-secondary" onClick={() => { setShowForwardContactModal(false); setForwardContactSearch(''); setSelectedContactToForward(null); setForwardListOpen(false); }}>
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleForwardContact}
                disabled={!selectedContactToForward || isForwardingContact}
              >
                {isForwardingContact ? 'Encaminhando...' : 'Encaminhar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segure na mensagem: responder ou reagir (1:1) */}
      {messageActionSheet && (
        <div
          className="cmob-message-action-overlay"
          onClick={() => setMessageActionSheet(null)}
          role="presentation"
        >
          <div className="cmob-message-action-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-message-action-title">Mensagem</div>
            <button
              type="button"
              className="cmob-message-action-btn"
              onClick={() => {
                handleReplyToMessage(messageActionSheet);
                setMessageActionSheet(null);
              }}
            >
              ↩ Responder
            </button>
            {messageActionSheet.mediaType !== 'system' && (
              <div className="cmob-message-action-reaction-wrap">
                <MessageReactionBar
                  variant="mobile"
                  onReact={(emoji) => {
                    void handleSendReaction(messageActionSheet, emoji);
                  }}
                  onRemoveReaction={() => {
                    void handleSendReaction(messageActionSheet, '');
                  }}
                />
              </div>
            )}
            <button type="button" className="cmob-message-action-cancel" onClick={() => setMessageActionSheet(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal de anexo: Baixar ou Responder */}
      {attachmentModal && (
        <div className="cmob-attachment-modal-overlay" onClick={() => setAttachmentModal(null)}>
          <div className={`cmob-attachment-modal cmob-attachment-modal--${attachmentModal.type}`} onClick={(e) => e.stopPropagation()}>
            <div className="cmob-attachment-modal-title">
              {attachmentModal.type === 'image' ? '🖼️ Imagem' : '📄 Documento'}
            </div>
            <div className="cmob-attachment-modal-filename">{attachmentModal.fileName}</div>
            <div className="cmob-attachment-modal-actions">
              {attachmentModal.type === 'image' && (
                <button
                  type="button"
                  className="cmob-attachment-modal-btn cmob-attachment-modal-view"
                  onClick={() => {
                    setSelectedImageUrl(attachmentModal.url);
                    setSelectedImageAlt(attachmentModal.fileName);
                    setShowImageViewer(true);
                    setAttachmentModal(null);
                  }}
                >
                  Visualizar
                </button>
              )}
              <button
                type="button"
                className="cmob-attachment-modal-btn cmob-attachment-modal-download"
                onClick={() => {
                  window.open(attachmentModal.url, '_blank');
                  setAttachmentModal(null);
                }}
              >
                Baixar
              </button>
              <button
                type="button"
                className="cmob-attachment-modal-btn cmob-attachment-modal-reply"
                onClick={() => {
                  if (attachmentModal.message) {
                    handleReplyToMessage(attachmentModal.message);
                  } else if (attachmentModal.groupMessage) {
                    handleReplyToGroupMessage(attachmentModal.groupMessage);
                  }
                  setAttachmentModal(null);
                }}
              >
                Responder
              </button>
              <button
                type="button"
                className="cmob-attachment-modal-btn cmob-attachment-modal-cancel"
                onClick={() => setAttachmentModal(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visualizador de Imagem */}
      <ImageViewer
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
        imageUrl={selectedImageUrl}
        imageAlt={selectedImageAlt}
      />

      {/* Visualizador de foto do contato (lista e header) */}
      {avatarViewerUrl && (
        <ImageViewer
          isOpen={showAvatarViewer}
          onClose={() => { setShowAvatarViewer(false); setAvatarViewerUrl(''); setAvatarViewerAlt(''); }}
          imageUrl={avatarViewerUrl}
          imageAlt={avatarViewerAlt}
        />
      )}
    </div>
  );
};

export default ChatMobile;
